import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { RichTextContentRenderer } from "../app/RichTextContentRenderer";
import { knowledgeArticleStatusLabel } from "../app/articleStatus";
import {
  healthClasses,
  priorityClasses,
  statusClasses,
  ticketPriorities,
  ticketStatuses,
} from "../app/data";
import { lockBodyScroll, unlockBodyScroll } from "../app/bodyScrollLock";
import { ticketDetailViewCopy } from "../app/i18n";
import { getIntlLocale, type AppLanguage } from "../app/language";
import { createRichTextContent, getRichTextPlainText } from "../app/richText";
import { AnimatedModal, TicketDurationText, TicketLastUpdateText, UserAvatarBadge } from "../app/ui";
import { appendTicketNote, parseTicketNotes } from "../app/ticketNotes";
import { QuillEditor, type QuillEditorHandle } from "../app/QuillEditor";
import {
  formatDateTimeForInput,
  formatScheduleRangeLabel,
  formatPreciseDateTimeLabel,
  formatRelativeDateTimeLabel,
  formatTicketSlaLabel,
  getInitials,
  getAssignableAssigneeNames,
  getTicketSlaCountdownInfo,
  getStatusIcon,
  normalizeDateTimeForStorage,
  resolveWorkspaceMember,
  syncTicketCustomFieldValues,
} from "../app/utils";
import type { Activity, Customer, KnowledgeArticle, ProductPlan, RichTextContent, Ticket, TicketAttachment, TicketAttachmentPublicShare, TicketCategory, TicketCategoryCustomField, TicketNote, TicketNoteAppendInput, TicketNoteKind, TicketPriority, TicketStatus, TicketUpdateInput, WorkspaceMember } from "../app/types";

type TicketDetailViewCopy = typeof ticketDetailViewCopy.en;

type TicketDetailViewProps = {
  language: AppLanguage;
  ticket: Ticket;
  customer: Customer;
  customers: Customer[];
  currentUser: string;
  timezone: string;
  isLoadingAttachments?: boolean;
  isSyncingTicket?: boolean;
  canEditTickets?: boolean;
  canDeleteTickets?: boolean;
  activities: Activity[];
  relatedTickets: Ticket[];
  knowledgeArticles: KnowledgeArticle[];
  productPlans: ProductPlan[];
  ticketCategories: TicketCategory[];
  teamMembers: WorkspaceMember[];
  onAssignTicket: (id: string, assignee: string | null) => void;
  onDeleteTicket: (id: string) => void;
  onUpdateCategory: (id: string, category: string) => void;
  onUpdateCustomFieldValues: (id: string, customFieldValues: Record<string, string>) => void;
  onUpdateDescription: (id: string, description: string) => void;
  onUpdateLinkedArticles: (id: string, linkedArticleIds: string[]) => void;
  onUpdateSchedule: (id: string, scheduleStartAt: string | null, scheduleAt: string | null) => void;
  onUpdateNotes: (id: string, updates: TicketUpdateInput) => void;
  onUpdateCustomer: (id: string, customerId: string) => void;
  onUpdateProduct: (id: string, product: string) => void;
  onUpdatePriority: (id: string, priority: TicketPriority) => void;
  onUpdateStatus: (id: string, status: TicketStatus) => void;
  onUploadAttachments: (id: string, files: FileList | File[], onProgress?: (percent: number) => void) => Promise<void>;
  onDeleteAttachment: (id: string, attachmentKey: string) => Promise<void>;
  attachmentOverridesEnabled?: boolean;
  attachmentPublicDefaultEnabled?: boolean;
  onSetAttachmentShare: (id: string, attachmentKey: string, override: TicketAttachmentPublicShare | null) => Promise<void>;
  onOpenArticle: (articleId: string, collectionId?: string | null) => void;
  onOpenCustomer: (customerId: string) => void;
  onOpenTicket: (ticketId: string) => void;
  onBack: () => void;
};

type TicketPillMenuProps<T extends string> = {
  value: T;
  options: readonly T[];
  ariaLabel: string;
  buttonClassName: string;
  onChange: (value: T) => void;
  renderValue: (value: T) => ReactNode;
  renderOption?: (value: T) => ReactNode;
  getOptionClassName?: (value: T) => string;
};

type AttachmentPreviewKind = "image" | "pdf" | "audio" | "video" | "text" | "unsupported";

const ATTACHMENT_IMAGE_EXTENSIONS = new Set(["apng", "avif", "bmp", "gif", "ico", "jpeg", "jpg", "png", "svg", "tif", "tiff", "webp"]);
const ATTACHMENT_TEXT_EXTENSIONS = new Set(["bat", "c", "conf", "cpp", "css", "csv", "env", "gitignore", "htm", "html", "ini", "java", "js", "json", "log", "md", "mjs", "py", "rb", "sh", "sql", "svg", "toml", "ts", "tsx", "txt", "xml", "yaml", "yml"]);
const ATTACHMENT_TEXT_CONTENT_TYPES = new Set([
  "application/javascript",
  "application/json",
  "application/ld+json",
  "application/sql",
  "application/typescript",
  "application/x-httpd-php",
  "application/x-sh",
  "application/xhtml+xml",
  "application/xml",
]);

function getTicketDetailCustomFieldError(customFields: TicketCategoryCustomField[], values: Record<string, string>, copy: TicketDetailViewCopy) {
  for (const field of customFields) {
    const value = values[field.id] ?? "";

    if (field.required && !value.trim()) {
      return copy.details.customFieldRequired(field.label);
    }
  }

  return null;
}

function createEmptyNoteContent(): RichTextContent {
  return null;
}

function TicketPillMenu<T extends string>({
  value,
  options,
  ariaLabel,
  buttonClassName,
  onChange,
  renderValue,
  renderOption,
  getOptionClassName,
}: TicketPillMenuProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (containerRef.current?.contains(event.target as Node)) {
        return;
      }

      setIsOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  return (
    <div ref={containerRef} className="relative" onClick={event => event.stopPropagation()}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={ariaLabel}
        onClick={() => setIsOpen(current => !current)}
        className={`${buttonClassName} transition-all hover:brightness-[0.98] focus:outline-none focus:ring-2 focus:ring-[#006949]/15`}
      >
        {renderValue(value)}
        <span aria-hidden="true" className="text-[9px] text-current/70">
          ▾
        </span>
      </button>

      {isOpen ? (
        <div className="absolute left-0 top-full z-20 mt-2 min-w-[11rem] rounded-[22px] border border-[#eaecf0] bg-white p-2 shadow-[0_24px_48px_rgba(15,23,42,0.14)]">
          {options.map(option => {
            const active = option === value;

            return (
              <button
                key={option}
                type="button"
                role="menuitemradio"
                aria-checked={active}
                onClick={() => {
                  onChange(option);
                  setIsOpen(false);
                }}
                className={`mt-1 flex w-full items-center justify-between rounded-2xl px-4 py-3 text-left text-sm font-semibold transition-colors first:mt-0 hover:bg-[#f8fafc] ${
                  getOptionClassName?.(option) ?? "text-[#101828]"
                } ${active ? "bg-[#f3f4f6]" : ""}`}
              >
                <span className="flex items-center gap-2">{renderOption ? renderOption(option) : renderValue(option)}</span>
                {active ? <span className="text-[#006949]">✓</span> : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function TicketDetailAssigneeIdentity({
  assignee,
  teamMembers,
  copy,
  avatarClassName = "brand-primary-avatar flex h-9 w-9 items-center justify-center rounded-full text-[10px] font-bold",
}: {
  assignee: string | null;
  teamMembers: WorkspaceMember[];
  copy: TicketDetailViewCopy;
  avatarClassName?: string;
}) {
  const member = resolveWorkspaceMember(teamMembers, assignee);

  return (
    <div className="flex items-center gap-3">
      <UserAvatarBadge
        avatarUrl={member?.avatarUrl ?? null}
        initials={member?.initials ?? getInitials(assignee ?? copy.unassigned)}
        className={avatarClassName}
      />
      <div className="min-w-0">
        <div className="truncate text-sm font-semibold text-[#101828]">{assignee ?? copy.unassigned}</div>
        <div className="text-[10px] font-medium uppercase tracking-[0.18em] text-slate-400">{assignee ? copy.assigned : copy.needsOwner}</div>
      </div>
    </div>
  );
}

function TicketDetailAssigneePicker({
  ticket,
  assigneeOptions,
  teamMembers,
  currentUserName,
  copy,
  onAssign,
}: {
  ticket: Ticket;
  assigneeOptions: string[];
  teamMembers: WorkspaceMember[];
  currentUserName: string;
  copy: TicketDetailViewCopy;
  onAssign: (assignee: string | null) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number; origin: "top" | "bottom" } | null>(null);

  const matchingAgents = assigneeOptions.filter(agent => {
    const normalizedSearch = search.trim().toLowerCase();

    if (ticket.assignee !== currentUserName && agent === currentUserName) {
      return false;
    }

    return normalizedSearch.length === 0 ? true : agent.toLowerCase().includes(normalizedSearch);
  });

  const updateMenuPosition = useCallback(() => {
    if (!triggerRef.current) {
      return;
    }

    const viewportPadding = 12;
    const estimatedMenuWidth = 256;
    const menuHeight = menuRef.current?.offsetHeight ?? 320;
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;
    const spaceAbove = rect.top - viewportPadding;
    const shouldOpenUpward = spaceBelow < menuHeight && spaceAbove > spaceBelow;
    const unclampedLeft = rect.left;
    const left = Math.min(
      Math.max(viewportPadding, unclampedLeft),
      window.innerWidth - estimatedMenuWidth - viewportPadding,
    );

    setMenuPosition({
      top: shouldOpenUpward ? Math.max(viewportPadding, rect.top - menuHeight - 8) : rect.bottom + 8,
      left,
      origin: shouldOpenUpward ? "bottom" : "top",
    });
  }, []);

  // Measure the rendered menu and position it before paint so the very first
  // open uses the menu's real height instead of the fallback estimate.
  useLayoutEffect(() => {
    if (!isOpen) {
      setMenuPosition(null);
      return;
    }

    updateMenuPosition();
  }, [isOpen, updateMenuPosition]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (containerRef.current?.contains(event.target as Node) || menuRef.current?.contains(event.target as Node)) {
        return;
      }

      setIsOpen(false);
      setSearch("");
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
        setSearch("");
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [isOpen]);

  const handleAssign = (assignee: string | null) => {
    onAssign(assignee);
    setIsOpen(false);
    setSearch("");
  };

  return (
    <div ref={containerRef} className="relative mt-3" onClick={event => event.stopPropagation()}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setIsOpen(current => !current)}
        className="flex w-full min-w-[14rem] items-start rounded-2xl border border-[#e4e7ec] bg-[#f9fafb] px-3 py-3 text-left transition-colors hover:border-slate-300 focus-visible:border-slate-300 focus-visible:outline-none"
      >
        <TicketDetailAssigneeIdentity assignee={ticket.assignee} teamMembers={teamMembers} copy={copy} avatarClassName="brand-primary-avatar flex h-10 w-10 items-center justify-center rounded-full text-[11px] font-bold" />
      </button>

      {isOpen
        ? createPortal(
            <div
              ref={menuRef}
              className="fixed z-[200] w-64 rounded-2xl border border-slate-200 bg-white p-2 shadow-[0_24px_48px_rgba(15,23,42,0.16)]"
              style={{
                top: menuPosition?.top ?? 0,
                left: menuPosition?.left ?? 0,
                visibility: menuPosition ? "visible" : "hidden",
              }}
              onMouseDown={event => event.stopPropagation()}
              onClick={event => event.stopPropagation()}
            >
              <div className="px-1 pb-2">
                <input
                  type="text"
                  value={search}
                  onChange={event => setSearch(event.target.value)}
                  onClick={event => event.stopPropagation()}
                  placeholder={copy.assignee.searchPlaceholder}
                  autoFocus
                  className="w-full rounded-xl border border-slate-200 bg-[#f9fafb] px-3 py-2 text-sm text-[#1a1c1d] outline-none transition focus:border-[#d0d5dd] focus:bg-white"
                />
              </div>

              <div className="space-y-1">
                {ticket.assignee !== null ? (
                  <button
                    type="button"
                    onClick={event => {
                      event.stopPropagation();
                      handleAssign(null);
                    }}
                    className="flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm font-semibold text-[#1a1c1d] transition-colors hover:bg-[#f3f4f6]"
                  >
                    <TicketDetailAssigneeIdentity assignee={null} teamMembers={teamMembers} copy={copy} avatarClassName="brand-primary-avatar flex h-8 w-8 items-center justify-center rounded-full text-[10px] font-bold" />
                  </button>
                ) : null}

                {ticket.assignee !== currentUserName ? (
                  <button
                    type="button"
                    onClick={event => {
                      event.stopPropagation();
                      handleAssign(currentUserName);
                    }}
                    className="flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm font-semibold text-[#1a1c1d] transition-colors hover:bg-[#f3f4f6]"
                  >
                    <TicketDetailAssigneeIdentity assignee={currentUserName} teamMembers={teamMembers} copy={copy} avatarClassName="brand-primary-avatar flex h-8 w-8 items-center justify-center rounded-full text-[10px] font-bold" />
                    <span className="brand-primary-text text-[10px] font-bold uppercase tracking-[0.2em]">{copy.you}</span>
                  </button>
                ) : null}

                <div className="max-h-56 overflow-y-auto">
                  {matchingAgents.map(agent => {
                    const isAssigned = agent === ticket.assignee;

                    return (
                      <button
                        key={agent}
                        type="button"
                        onClick={event => {
                          event.stopPropagation();
                          handleAssign(agent);
                        }}
                        className="flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm text-[#1a1c1d] transition-colors hover:bg-[#f3f4f6]"
                      >
                        <TicketDetailAssigneeIdentity assignee={agent} teamMembers={teamMembers} copy={copy} avatarClassName="brand-primary-avatar flex h-8 w-8 items-center justify-center rounded-full text-[10px] font-bold" />
                        {isAssigned ? <span className="brand-primary-text text-xs font-semibold">{copy.assigned}</span> : null}
                      </button>
                    );
                  })}

                  {matchingAgents.length === 0 ? <p className="px-3 py-2 text-sm text-slate-400">{copy.noMatchesFound}</p> : null}
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

function getRelatedActivity(ticket: Ticket, activities: Activity[]) {
  return activities;
}

function formatTicketNoteTimestamp(note: TicketNote, timezone: string, copy: TicketDetailViewCopy) {
  if (note.createdAt) {
    return formatPreciseDateTimeLabel(note.createdAt, note.legacyTimestampLabel ?? copy.notes.existingCaseNote, timezone);
  }

  return note.legacyTimestampLabel ?? copy.notes.existingCaseNote;
}

function getTicketNoteComposerTheme(kind: TicketNoteKind, copy: TicketDetailViewCopy) {
  if (kind === "external") {
    return {
      surfaceClassName: "ticket-note-composer--external",
      headerClassName: "ticket-note-composer-header--external",
      selectedOptionClassName: "ticket-note-toggle--active ticket-note-toggle--external",
      identityChipClassName: "ticket-note-chip--external",
      accentClassName: "ticket-note-chip-accent ticket-note-chip-accent--external",
      submitButtonClassName: "ticket-note-submit ticket-note-submit--external",
      helperChipClassName: "ticket-note-context-pill ticket-note-context-pill--external",
      helperChipLabel: copy.notes.helperChips.external,
    };
  }

  return {
    surfaceClassName: "ticket-note-composer--internal",
    headerClassName: "ticket-note-composer-header--internal",
    selectedOptionClassName: "ticket-note-toggle--active ticket-note-toggle--internal",
    identityChipClassName: "ticket-note-chip--internal",
    accentClassName: "ticket-note-chip-accent ticket-note-chip-accent--internal",
    submitButtonClassName: "ticket-note-submit ticket-note-submit--internal",
    helperChipClassName: "ticket-note-context-pill ticket-note-context-pill--internal",
    helperChipLabel: copy.notes.helperChips.internal,
  };
}

function getTicketNoteAppearance(note: TicketNote, copy: TicketDetailViewCopy) {
  if (note.isLegacy) {
    return {
      cardClassName: "ticket-note-entry--legacy",
      avatarClassName: "ticket-note-avatar--legacy",
      kindBadgeClassName: "ticket-note-badge ticket-note-badge--legacy",
      kindLabel: copy.notes.imported,
      metaBadgeClassName: null,
      metaBadgeLabel: null,
      bodyClassName: "ticket-note-body--legacy",
    };
  }

  if (note.kind === "external") {
    if (note.deliveryStatus === "received") {
      return {
        cardClassName: "ticket-note-entry--customer",
        avatarClassName: "ticket-note-avatar--customer",
        kindBadgeClassName: "ticket-note-badge ticket-note-badge--customer",
        kindLabel: copy.notes.customer,
        metaBadgeClassName: "ticket-note-meta ticket-note-meta--customer",
        metaBadgeLabel: copy.notes.customerReplyMeta,
        bodyClassName: "ticket-note-body--customer",
      };
    }

    return {
      cardClassName: "ticket-note-entry--external",
      avatarClassName: "ticket-note-avatar--external",
      kindBadgeClassName: "ticket-note-badge ticket-note-badge--external",
      kindLabel: copy.notes.external,
      metaBadgeClassName: "ticket-note-meta ticket-note-meta--external",
      metaBadgeLabel: copy.notes.externalMeta,
      bodyClassName: "ticket-note-body--external",
    };
  }

  return {
    cardClassName: "ticket-note-entry--internal",
    avatarClassName: "ticket-note-avatar--internal",
    kindBadgeClassName: "ticket-note-badge ticket-note-badge--internal",
    kindLabel: copy.notes.internal,
    metaBadgeClassName: null,
    metaBadgeLabel: null,
    bodyClassName: "ticket-note-body--internal",
  };
}

function compareText(left: string, right: string) {
  return left.localeCompare(right, undefined, { sensitivity: "base" });
}

function hasTicketItem(productName: string | null | undefined, noneLabel: string) {
  const normalizedProductName = productName?.trim() ?? "";
  const normalizedNoneLabel = noneLabel.trim().toLowerCase();

  return Boolean(normalizedProductName)
    && normalizedProductName.toLowerCase() !== normalizedNoneLabel
    && normalizedProductName.toLowerCase() !== "none";
}

function getTicketItemDisplayLabel(productName: string | null | undefined, emptyLabel: string, noneLabel: string) {
  return hasTicketItem(productName, noneLabel) ? productName!.trim() : emptyLabel;
}

function getTicketItemOptions(currentProduct: string, customer: Customer | null | undefined, productPlans: ProductPlan[], noneLabel: string) {
  return [...new Set([
    ...(hasTicketItem(currentProduct, noneLabel) ? [currentProduct.trim()] : []),
    ...(customer?.products.map(product => product.name).filter(Boolean) ?? []),
    ...productPlans.map(plan => plan.name).filter(Boolean),
  ])].sort(compareText);
}

const maxTicketAttachmentTotalBytes = 100 * 1024 * 1024;

function formatAttachmentSize(size: number) {
  if (size >= 1024 * 1024) {
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  }

  if (size >= 1024) {
    return `${Math.round(size / 1024)} KB`;
  }

  return `${size} B`;
}

function formatAttachmentTimestamp(value: string, language: AppLanguage) {
  const parsed = Date.parse(value);

  if (Number.isNaN(parsed)) {
    return value;
  }

  return new Intl.DateTimeFormat(getIntlLocale(language), {
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(parsed));
}

function getAttachmentExtension(name: string) {
  const normalizedName = name.trim().toLowerCase();
  const lastDotIndex = normalizedName.lastIndexOf(".");

  if (lastDotIndex < 0 || lastDotIndex === normalizedName.length - 1) {
    return "";
  }

  return normalizedName.slice(lastDotIndex + 1);
}

function isTextAttachmentContentType(contentType: string) {
  if (!contentType) {
    return false;
  }

  if (contentType.startsWith("text/")) {
    return true;
  }

  if (ATTACHMENT_TEXT_CONTENT_TYPES.has(contentType)) {
    return true;
  }

  return /\b(json|xml|yaml|javascript|typescript|sql|shellscript)\b/.test(contentType);
}

function getAttachmentPreviewKind(attachment: TicketAttachment): AttachmentPreviewKind {
  const contentType = attachment.contentType?.toLowerCase() ?? "";
  const extension = getAttachmentExtension(attachment.name);

  if (contentType.startsWith("image/") || ATTACHMENT_IMAGE_EXTENSIONS.has(extension)) {
    return "image";
  }

  if (contentType === "application/pdf" || extension === "pdf") {
    return "pdf";
  }

  if (contentType.startsWith("audio/")) {
    return "audio";
  }

  if (contentType.startsWith("video/")) {
    return "video";
  }

  if (isTextAttachmentContentType(contentType) || ATTACHMENT_TEXT_EXTENSIONS.has(extension)) {
    return "text";
  }

  return "unsupported";
}

export function TicketDetailView({
  language,
  ticket,
  customer,
  customers,
  currentUser,
  timezone,
  isLoadingAttachments = false,
  isSyncingTicket = false,
  canEditTickets = true,
  canDeleteTickets = false,
  activities,
  relatedTickets,
  knowledgeArticles = [],
  productPlans,
  ticketCategories,
  teamMembers,
  onAssignTicket,
  onDeleteTicket,
  onUpdateCategory,
  onUpdateCustomFieldValues,
  onUpdateDescription,
  onUpdateLinkedArticles,
  onUpdateSchedule,
  onUpdateNotes,
  onUpdateCustomer,
  onUpdateProduct,
  onUpdatePriority,
  onUpdateStatus,
  onUploadAttachments,
  onDeleteAttachment,
  attachmentOverridesEnabled = false,
  attachmentPublicDefaultEnabled = false,
  onSetAttachmentShare,
  onOpenArticle,
  onOpenCustomer,
  onOpenTicket,
  onBack,
}: TicketDetailViewProps) {
  const copy = ticketDetailViewCopy[language];
  const hasCustomer = Boolean(ticket.customerId.trim() && customer.id.trim());
  const [currentTime, setCurrentTime] = useState(() => Date.now());
  const [isActivityModalOpen, setIsActivityModalOpen] = useState(false);
  const [isRelatedTicketsModalOpen, setIsRelatedTicketsModalOpen] = useState(false);
  const ticketActivities = getRelatedActivity(ticket, activities);
  const activityPreview = ticketActivities.slice(0, 2);
  const relatedTicketsPreview = relatedTickets.slice(0, 2);
  const ticketCategoryNames = ticketCategories.length > 0 ? ticketCategories.map(category => category.name) : [ticket.category || "General"];
  const selectedCategory = ticketCategories.find(category => category.name.trim().toLowerCase() === ticket.category.trim().toLowerCase()) ?? null;
  const itemOptions = getTicketItemOptions(ticket.product, customer, productPlans, copy.none);
  const ticketCustomFields = selectedCategory?.customFields ?? [];
  const caseNotes = parseTicketNotes(ticket.notes);
  const ticketCreatedAt = ticket.createdAt;
  const assignableAssignees = getAssignableAssigneeNames(teamMembers, ticketCategories, ticket.category);
  const currentWorkspaceMember = resolveWorkspaceMember(teamMembers, currentUser);
  const [descriptionDraft, setDescriptionDraft] = useState(ticket.description);
  const [customFieldDraft, setCustomFieldDraft] = useState(() => syncTicketCustomFieldValues(ticketCustomFields, ticket.customFieldValues));
  const [customFieldError, setCustomFieldError] = useState<string | null>(null);
  const [scheduleStartDraft, setScheduleStartDraft] = useState(formatDateTimeForInput(ticket.scheduleStartAt, timezone));
  const [scheduleDraft, setScheduleDraft] = useState(formatDateTimeForInput(ticket.scheduleAt, timezone));
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [customerSearch, setCustomerSearch] = useState("");
  const [selectedCustomerIdDraft, setSelectedCustomerIdDraft] = useState(() => (hasCustomer ? customer.id : ""));
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const noteEditorRef = useRef<QuillEditorHandle | null>(null);
  const [noteDraftContent, setNoteDraftContent] = useState<RichTextContent>(() => createEmptyNoteContent());
  const [isNoteEditorReady, setIsNoteEditorReady] = useState(false);
  const [noteEditorError, setNoteEditorError] = useState<string | null>(null);
  const [noteKind, setNoteKind] = useState<TicketNoteKind>("internal");
  const [articleSearch, setArticleSearch] = useState("");
  const [selectedArticleModal, setSelectedArticleModal] = useState<KnowledgeArticle | null>(null);
  const [selectedAttachmentModal, setSelectedAttachmentModal] = useState<TicketAttachment | null>(null);
  const [isUploadingAttachments, setIsUploadingAttachments] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [deletingAttachmentKeys, setDeletingAttachmentKeys] = useState<string[]>([]);
  const [copiedAttachmentKey, setCopiedAttachmentKey] = useState<string | null>(null);
  const [copiedPublicAttachmentKey, setCopiedPublicAttachmentKey] = useState<string | null>(null);
  const [updatingShareKeys, setUpdatingShareKeys] = useState<string[]>([]);
  const [attachmentTextPreview, setAttachmentTextPreview] = useState<string | null>(null);
  const [attachmentTextPreviewError, setAttachmentTextPreviewError] = useState<string | null>(null);
  const [isLoadingAttachmentTextPreview, setIsLoadingAttachmentTextPreview] = useState(false);
  const [attachmentPdfPreviewUrl, setAttachmentPdfPreviewUrl] = useState<string | null>(null);
  const [attachmentPdfPreviewError, setAttachmentPdfPreviewError] = useState<string | null>(null);
  const [isLoadingAttachmentPdfPreview, setIsLoadingAttachmentPdfPreview] = useState(false);
  const noteComposerTheme = getTicketNoteComposerTheme(noteKind, copy);
  const formattedScheduleStartValue = formatDateTimeForInput(ticket.scheduleStartAt, timezone);
  const formattedScheduleValue = formatDateTimeForInput(ticket.scheduleAt, timezone);
  const linkedArticleIds = Array.isArray(ticket.linkedArticleIds) ? ticket.linkedArticleIds : [];
  const ticketAttachments = Array.isArray(ticket.attachments) ? ticket.attachments : [];
  const publicAttachmentDefaultStateLabel = attachmentPublicDefaultEnabled ? copy.attachments.shareDefaultAllowed : copy.attachments.shareDefaultDenied;
  const normalizedCustomerSearch = customerSearch.trim().toLowerCase();
  const filteredCustomers = customers
    .filter(item => {
      if (!normalizedCustomerSearch) {
        return true;
      }

      return [item.company, item.name, item.email, item.region]
        .filter(Boolean)
        .some(value => value.toLowerCase().includes(normalizedCustomerSearch));
    })
    .sort((left, right) => compareText(left.company || left.name, right.company || right.name));
  const searchableArticles = Array.isArray(knowledgeArticles) ? knowledgeArticles : [];
  const normalizedArticleSearch = articleSearch.trim().toLowerCase();
  const linkedArticles = linkedArticleIds
    .map(articleId => searchableArticles.find(article => article.id === articleId) ?? null)
    .filter((article): article is KnowledgeArticle => article !== null);
  const matchingArticles = searchableArticles
    .filter(article => !linkedArticleIds.includes(article.id))
    .filter(article => {
      if (!normalizedArticleSearch) {
        return false;
      }

      return [article.title, article.summary, article.meta, article.audience, article.status, article.estimatedReadTime, ...article.keywords]
        .some(value => value.toLowerCase().includes(normalizedArticleSearch));
    })
    .sort((left, right) => {
      const leftCategoryMatch = left.audience.toLowerCase() === ticket.category.toLowerCase() ? 1 : 0;
      const rightCategoryMatch = right.audience.toLowerCase() === ticket.category.toLowerCase() ? 1 : 0;

      if (leftCategoryMatch !== rightCategoryMatch) {
        return rightCategoryMatch - leftCategoryMatch;
      }

      return left.title.localeCompare(right.title);
    })
    .slice(0, 8);
  const slaCountdown = getTicketSlaCountdownInfo(ticket.createdAt, ticket.sla, currentTime, ticket.slaPausedAt, ticket.slaPausedTotalMs);

  const handleNoteEditorChange = (html: string) => {
    setNoteDraftContent(createRichTextContent(html));
  };

  const handleCopyAttachmentLink = async (attachment: TicketAttachment) => {
    setAttachmentError(null);

    const absoluteUrl = typeof window !== "undefined"
      ? new URL(attachment.downloadUrl, window.location.origin).toString()
      : attachment.downloadUrl;

    try {
      if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(absoluteUrl);
      } else {
        throw new Error("Clipboard unavailable");
      }

      setCopiedAttachmentKey(attachment.key);
      window.setTimeout(() => {
        setCopiedAttachmentKey(current => (current === attachment.key ? null : current));
      }, 2000);
    } catch {
      setAttachmentError(copy.attachments.linkCopyError);
    }
  };

  const handleCopyPublicAttachmentLink = async (attachment: TicketAttachment) => {
    if (!attachment.publicDownloadUrl) {
      return;
    }

    setAttachmentError(null);

    const absoluteUrl = typeof window !== "undefined"
      ? new URL(attachment.publicDownloadUrl, window.location.origin).toString()
      : attachment.publicDownloadUrl;

    try {
      if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(absoluteUrl);
      } else {
        throw new Error("Clipboard unavailable");
      }

      setCopiedPublicAttachmentKey(attachment.key);
      window.setTimeout(() => {
        setCopiedPublicAttachmentKey(current => (current === attachment.key ? null : current));
      }, 2000);
    } catch {
      setAttachmentError(copy.attachments.linkCopyError);
    }
  };

  const handleAttachmentShareChange = async (attachment: TicketAttachment, override: TicketAttachmentPublicShare | null) => {
    setAttachmentError(null);
    setUpdatingShareKeys(current => (current.includes(attachment.key) ? current : [...current, attachment.key]));

    try {
      await onSetAttachmentShare(ticket.id, attachment.key, override);
    } catch (error) {
      setAttachmentError(error instanceof Error ? error.message : copy.attachments.shareUpdateError);
    } finally {
      setUpdatingShareKeys(current => current.filter(key => key !== attachment.key));
    }
  };

  useEffect(() => {
    setDescriptionDraft(ticket.description);
    setCustomFieldDraft(syncTicketCustomFieldValues(ticketCustomFields, ticket.customFieldValues));
    setCustomFieldError(null);
    setScheduleStartDraft(formatDateTimeForInput(ticket.scheduleStartAt, timezone));
    setScheduleDraft(formatDateTimeForInput(ticket.scheduleAt, timezone));
    setIsScheduleModalOpen(false);
    setIsCustomerModalOpen(false);
    setCustomerSearch("");
    setSelectedCustomerIdDraft(hasCustomer ? customer.id : "");
    setIsActivityModalOpen(false);
    setIsRelatedTicketsModalOpen(false);
    setIsEditingDescription(false);
    const nextNoteContent = createEmptyNoteContent();
    setNoteDraftContent(nextNoteContent);
    setNoteEditorError(null);
    setNoteKind("internal");
    setArticleSearch("");
    setSelectedArticleModal(null);
    setSelectedAttachmentModal(null);
    setIsUploadingAttachments(false);
    setAttachmentError(null);
    setDeletingAttachmentKeys([]);
    setAttachmentTextPreview(null);
    setAttachmentTextPreviewError(null);
    setIsLoadingAttachmentTextPreview(false);
    setAttachmentPdfPreviewUrl(null);
    setAttachmentPdfPreviewError(null);
    setIsLoadingAttachmentPdfPreview(false);

    noteEditorRef.current?.clear();
  }, [customer.id, hasCustomer, ticket.id, ticket.description, ticket.scheduleAt, ticket.customFieldValues, ticketCustomFields, timezone]);

  const closeCustomerModal = () => {
    setIsCustomerModalOpen(false);
    setCustomerSearch("");
    setSelectedCustomerIdDraft(hasCustomer ? customer.id : "");
  };

  const openCustomerModal = () => {
    setSelectedCustomerIdDraft(hasCustomer ? customer.id : "");
    setCustomerSearch("");
    setIsCustomerModalOpen(true);
  };

  const handleSaveCustomer = () => {
    onUpdateCustomer(ticket.id, selectedCustomerIdDraft);
    closeCustomerModal();
  };

  useEffect(() => {
    setCurrentTime(Date.now());

    const intervalId = window.setInterval(() => {
      setCurrentTime(Date.now());
    }, 60_000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [ticket.id, ticket.createdAt, ticket.sla, ticket.slaPausedAt, ticket.slaPausedTotalMs]);

  useEffect(() => {
    if (!isScheduleModalOpen || typeof document === "undefined") {
      return;
    }

    lockBodyScroll();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsScheduleModalOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      unlockBodyScroll();
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isScheduleModalOpen]);

  useEffect(() => {
    if (!isActivityModalOpen || typeof document === "undefined") {
      return;
    }

    lockBodyScroll();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsActivityModalOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      unlockBodyScroll();
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isActivityModalOpen]);

  useEffect(() => {
    if (!isRelatedTicketsModalOpen || typeof document === "undefined") {
      return;
    }

    lockBodyScroll();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsRelatedTicketsModalOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      unlockBodyScroll();
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isRelatedTicketsModalOpen]);

  useEffect(() => {
    if (!selectedArticleModal || typeof document === "undefined") {
      return;
    }

    lockBodyScroll();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setSelectedArticleModal(null);
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      unlockBodyScroll();
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedArticleModal]);

  useEffect(() => {
    if (!selectedAttachmentModal || typeof document === "undefined") {
      return;
    }

    lockBodyScroll();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setSelectedAttachmentModal(null);
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      unlockBodyScroll();
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedAttachmentModal]);

  useEffect(() => {
    if (!selectedAttachmentModal) {
      setAttachmentTextPreview(null);
      setAttachmentTextPreviewError(null);
      setIsLoadingAttachmentTextPreview(false);
      return;
    }

    if (getAttachmentPreviewKind(selectedAttachmentModal) !== "text") {
      setAttachmentTextPreview(null);
      setAttachmentTextPreviewError(null);
      setIsLoadingAttachmentTextPreview(false);
      return;
    }

    const abortController = new AbortController();

    setAttachmentTextPreview(null);
    setAttachmentTextPreviewError(null);
    setIsLoadingAttachmentTextPreview(true);

    fetch(selectedAttachmentModal.downloadUrl, { signal: abortController.signal })
      .then(async response => {
        if (!response.ok) {
          throw new Error(`Unable to load preview (${response.status})`);
        }

        return response.text();
      })
      .then(text => {
        if (!abortController.signal.aborted) {
          setAttachmentTextPreview(text);
        }
      })
      .catch(error => {
        if (abortController.signal.aborted) {
          return;
        }

        setAttachmentTextPreviewError(error instanceof Error ? error.message : copy.attachments.unableToLoadTextPreview);
      })
      .finally(() => {
        if (!abortController.signal.aborted) {
          setIsLoadingAttachmentTextPreview(false);
        }
      });

    return () => {
      abortController.abort();
    };
  }, [selectedAttachmentModal]);

  useEffect(() => {
    if (!selectedAttachmentModal) {
      setAttachmentPdfPreviewUrl(null);
      setAttachmentPdfPreviewError(null);
      setIsLoadingAttachmentPdfPreview(false);
      return;
    }

    if (getAttachmentPreviewKind(selectedAttachmentModal) !== "pdf") {
      setAttachmentPdfPreviewUrl(null);
      setAttachmentPdfPreviewError(null);
      setIsLoadingAttachmentPdfPreview(false);
      return;
    }

    const abortController = new AbortController();
    let objectUrl: string | null = null;

    setAttachmentPdfPreviewUrl(null);
    setAttachmentPdfPreviewError(null);
    setIsLoadingAttachmentPdfPreview(true);

    fetch(selectedAttachmentModal.downloadUrl, { signal: abortController.signal })
      .then(async response => {
        if (!response.ok) {
          throw new Error(`Unable to load preview (${response.status})`);
        }

        const blob = await response.blob();
        objectUrl = URL.createObjectURL(blob.type ? blob : new Blob([blob], { type: "application/pdf" }));

        if (!abortController.signal.aborted) {
          setAttachmentPdfPreviewUrl(objectUrl);
        }
      })
      .catch(error => {
        if (abortController.signal.aborted) {
          return;
        }

        setAttachmentPdfPreviewError(error instanceof Error ? error.message : copy.attachments.unableToLoadPdfPreview);
      })
      .finally(() => {
        if (!abortController.signal.aborted) {
          setIsLoadingAttachmentPdfPreview(false);
        }
      });

    return () => {
      abortController.abort();

      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [selectedAttachmentModal]);

  const hasPendingDescription = descriptionDraft.trim() !== ticket.description.trim();
  const hasPendingCustomFields = JSON.stringify(customFieldDraft) !== JSON.stringify(syncTicketCustomFieldValues(ticketCustomFields, ticket.customFieldValues));
  const hasPendingSchedule = scheduleDraft !== formattedScheduleValue || scheduleStartDraft !== formattedScheduleStartValue;
  const hasPendingNote = getRichTextPlainText(noteDraftContent).trim().length > 0;

  const handleSubmitNote = async () => {
    const capturedHtml = noteEditorRef.current?.getHTML();
    const nextContent = capturedHtml !== undefined ? createRichTextContent(capturedHtml) : noteDraftContent;
    const noteBody = getRichTextPlainText(nextContent);
    const noteInput: TicketNoteAppendInput = {
      author: currentUser,
      body: noteBody,
      bodyContent: nextContent?.html ? nextContent : null,
      kind: noteKind,
    };
    const result = appendTicketNote(ticket.notes, noteInput);

    if (!result.note) {
      return;
    }

    const appendedNotePayload: TicketNoteAppendInput = {
      ...noteInput,
      createdAt: result.note.createdAt,
      id: result.note.id,
    };

    onUpdateNotes(ticket.id, {
      notes: result.notes,
      appendedNote: appendedNotePayload,
      ...(noteKind === "external" && ticket.sla && !ticket.slaPausedAt
        ? {
            slaPausedAt: result.note.createdAt,
            slaPausedTotalMs: ticket.slaPausedTotalMs,
          }
        : {}),
    });

    const resetContent = createEmptyNoteContent();
    setNoteDraftContent(resetContent);
    setNoteKind("internal");
    noteEditorRef.current?.clear();
  };

  return (
    <section className="space-y-8 p-8">
      <div className="flex flex-wrap items-start justify-between gap-6">
        <div>
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#475467] shadow-[0_12px_28px_rgba(15,23,42,0.06)] transition-colors hover:bg-[#f8fafc]"
          >
            <span>←</span>
            <span>{copy.page.backToTickets}</span>
          </button>

          <p className="mt-5 text-[11px] font-semibold uppercase tracking-[0.32em] text-[#98a2b3]">{copy.page.eyebrow}</p>
            <div className="mt-3 space-y-2">
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="font-headline text-5xl font-extrabold tracking-[-0.06em] text-[#101828]">#{ticket.id}</h1>
                {isSyncingTicket ? (
                  <span className="inline-flex items-center rounded-full bg-[var(--brand-primary-soft)] px-2.5 py-2 text-[var(--brand-primary)]" aria-label="Syncing" title="Syncing">
                    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-3.5 w-3.5 animate-spin" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="10" cy="10" r="7" stroke="currentColor" strokeOpacity="0.2" strokeWidth="2" />
                      <path d="M10 3a7 7 0 0 1 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  </span>
                ) : null}
                {canEditTickets ? (
                  <>
                    <TicketPillMenu
                      value={ticket.priority}
                      options={ticketPriorities}
                      ariaLabel={copy.page.changePriority}
                      onChange={priority => onUpdatePriority(ticket.id, priority)}
                      buttonClassName={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wide ${priorityClasses[ticket.priority]}`}
                      renderValue={priority => <span>{copy.priorityLabels[priority]}</span>}
                      renderOption={priority => <span className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wide ${priorityClasses[priority]}`}>{copy.priorityLabels[priority]}</span>}
                    />
                    <TicketPillMenu
                      value={ticket.status}
                      options={ticketStatuses}
                      ariaLabel={copy.page.changeStatus}
                      onChange={status => onUpdateStatus(ticket.id, status)}
                      buttonClassName={`inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-[10px] font-bold uppercase tracking-wide shadow-[0_8px_20px_rgba(15,23,42,0.05)] ${statusClasses[ticket.status]}`}
                      renderValue={status => (
                        <>
                          <span className="text-xs">{getStatusIcon(status)}</span>
                          <span>{copy.statusLabels[status]}</span>
                        </>
                      )}
                      getOptionClassName={status => statusClasses[status]}
                      renderOption={status => (
                        <>
                          <span className="text-base leading-none">{getStatusIcon(status)}</span>
                          <span>{copy.statusLabels[status]}</span>
                        </>
                      )}
                    />
                    {itemOptions.length > 0 ? (
                      <TicketPillMenu
                        value={ticket.product}
                        options={itemOptions}
                        ariaLabel={copy.page.changeItem}
                        onChange={product => onUpdateProduct(ticket.id, product)}
                        buttonClassName="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-[#475467] shadow-[0_8px_20px_rgba(15,23,42,0.05)]"
                        renderValue={product => <span>{getTicketItemDisplayLabel(product, copy.page.emptyItem, copy.none)}</span>}
                        renderOption={product => <span>{product}</span>}
                      />
                    ) : null}
                  </>
                ) : (
                  <>
                    <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wide ${priorityClasses[ticket.priority]}`}>{copy.priorityLabels[ticket.priority]}</span>
                    <span className={`inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-[10px] font-bold uppercase tracking-wide shadow-[0_8px_20px_rgba(15,23,42,0.05)] ${statusClasses[ticket.status]}`}>{copy.statusLabels[ticket.status]}</span>
                  </>
                )}
                {!canEditTickets || itemOptions.length === 0 ? (
                  <span className="inline-flex items-center rounded-full bg-white px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-[#475467] shadow-[0_8px_20px_rgba(15,23,42,0.05)]">
                    {getTicketItemDisplayLabel(ticket.product, copy.page.emptyItem, copy.none)}
                  </span>
                ) : null}
                {ticket.sla ? (
                  <span className="inline-flex items-center rounded-full bg-[#ecfdf3] px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-[#027a48]">
                    SLA · {formatTicketSlaLabel(ticket.sla)}
                  </span>
                ) : null}
                {slaCountdown ? (
                  <span className={`inline-flex items-center rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wide ${slaCountdown.isPaused ? "bg-[#fff7ed] text-[#c4320a]" : slaCountdown.isOverdue ? "bg-[#fef3f2] text-[#b42318]" : "bg-[#eff8ff] text-[#175cd3]"}`}>
                    {slaCountdown.isPaused ? `${copy.page.paused} · ${slaCountdown.displayLabel} ${copy.page.left}` : `${slaCountdown.displayLabel} ${slaCountdown.isOverdue ? copy.page.over : copy.page.left}`}
                  </span>
                ) : null}
                {ticket.scheduleAt ? (
                  <span className="inline-flex items-center rounded-full bg-[#fef3c7] px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-[#b54708]">
                    {copy.page.scheduled}
                  </span>
                ) : null}
              </div>
              <p className="text-lg font-semibold text-[#475467]">{ticket.subject || copy.page.untitled}</p>
          </div>
        </div>

        <div className="flex flex-wrap items-stretch gap-3">
          {canDeleteTickets ? (
            <button
              type="button"
              aria-label={copy.page.deleteTicket}
              title={copy.page.deleteTicket}
              onClick={event => {
                event.preventDefault();
                onDeleteTicket(ticket.id);
              }}
              className="flex h-11 w-11 shrink-0 self-center items-center justify-center rounded-full border border-white/60 bg-white text-[#b42318] shadow-[0_20px_40px_rgba(26,28,29,0.04)] transition-all hover:border-[#fecaca] hover:bg-[#fef2f2] hover:text-[#912018] focus:outline-none focus:ring-2 focus:ring-[#b42318]/15 disabled:cursor-not-allowed disabled:text-[#d0d5dd] disabled:hover:border-white/60 disabled:hover:bg-white"
            >
              <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-4 w-4" xmlns="http://www.w3.org/2000/svg">
                <path d="M5.833 7.5v6.25A1.667 1.667 0 0 0 7.5 15.417h5a1.667 1.667 0 0 0 1.667-1.667V7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M4.167 5.417h11.666" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M7.917 5.417V4.583c0-.46.373-.833.833-.833h2.5c.46 0 .833.373.833.833v.834" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M8.75 9.583v3.334" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M11.25 9.583v3.334" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              <span className="sr-only">{copy.page.deleteTicket}</span>
            </button>
          ) : null}
          <label className="rounded-[24px] border border-white/60 bg-white px-4 py-3 shadow-[0_20px_40px_rgba(26,28,29,0.04)]">
            <span className="flex items-center justify-between gap-3">
              <span className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">{copy.assignee.label}</span>
            </span>
            {canEditTickets ? (
              <TicketDetailAssigneePicker
                ticket={ticket}
                assigneeOptions={assignableAssignees}
                teamMembers={teamMembers}
                currentUserName={currentUser}
                copy={copy}
                onAssign={assignee => onAssignTicket(ticket.id, assignee)}
              />
            ) : (
              <div className="mt-3 rounded-2xl border border-[#e4e7ec] bg-[#f9fafb] px-3 py-3">
                <TicketDetailAssigneeIdentity
                  assignee={ticket.assignee}
                  teamMembers={teamMembers}
                  copy={copy}
                  avatarClassName="brand-primary-avatar flex h-10 w-10 items-center justify-center rounded-full text-[11px] font-bold"
                />
              </div>
            )}
          </label>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1.24fr)_minmax(200px,0.48fr)]">
        <div className="space-y-6">
          <section className="rounded-[32px] border border-white/60 bg-white px-6 pb-6 pt-4 shadow-[0_20px_40px_rgba(26,28,29,0.04)]">
            <div className="rounded-[28px]">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">{copy.description.title}</p>
                {canEditTickets ? (
                    <button
                    type="button"
                    onClick={() => {
                      if (!isEditingDescription) {
                        setDescriptionDraft(ticket.description);
                        setIsEditingDescription(true);
                        return;
                      }

                      if (hasPendingDescription) {
                        onUpdateDescription(ticket.id, descriptionDraft.trim());
                      }

                      setIsEditingDescription(false);
                    }}
                      className={`ticket-description-action rounded-xl px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] transition-all ${
                      isEditingDescription || hasPendingDescription
                        ? "brand-primary-button text-white"
                        : "bg-white text-[#475467] shadow-[0_8px_20px_rgba(15,23,42,0.04)] hover:bg-[#f8fafc]"
                    }`}
                  >
                    {isEditingDescription ? copy.description.save : copy.description.edit}
                  </button>
                ) : null}
              </div>

              <div className="mt-3">
                {isEditingDescription && canEditTickets ? (
                  <textarea
                    value={descriptionDraft}
                    onChange={event => setDescriptionDraft(event.target.value)}
                    placeholder={copy.description.placeholder}
                    rows={4}
                    className="ticket-editor-input w-full rounded-[22px] border border-white/80 bg-white px-4 py-4 text-sm leading-7 text-[#334155] outline-none shadow-[0_12px_28px_rgba(15,23,42,0.05)] transition placeholder:text-[#98a2b3] placeholder:italic focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[var(--brand-primary-ring)]"
                  />
                ) : descriptionDraft.trim() ? (
                  <div className="ticket-description-body rounded-[20px] border border-[#e4e7ec] bg-[#fcfcfd] px-4 py-4 shadow-[0_10px_24px_rgba(15,23,42,0.05)]">
                    <p className="whitespace-pre-line text-sm leading-7 text-[#475467]">{descriptionDraft}</p>
                  </div>
                ) : (
                  <div className="ticket-description-body rounded-[20px] border border-[#e4e7ec] bg-[#fcfcfd] px-4 py-4 shadow-[0_10px_24px_rgba(15,23,42,0.05)]">
                    <p className="text-sm text-[#98a2b3]">{copy.description.empty}</p>
                  </div>
                )}
              </div>

              <div className="ticket-linked-surface mt-5 overflow-hidden rounded-[28px] border border-[#e4e7ec] bg-[linear-gradient(180deg,#ffffff,#fcfcfd)] shadow-[0_16px_32px_rgba(15,23,42,0.04)]">
                <div className="flex flex-wrap items-center justify-between gap-3 px-6 pb-3 pt-6">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">{copy.linkedArticles.title}</p>
                  </div>
                  <span className="ticket-linked-badge rounded-full bg-white px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#475467] shadow-[0_8px_20px_rgba(15,23,42,0.04)]">
                    {copy.linkedArticles.linkedCount(linkedArticles.length)}
                  </span>
                </div>

                <div className="px-6 pb-6 pt-1">
                  {canEditTickets ? (
                    <div className="space-y-3">
                      <label className="block">
                        <span className="sr-only">{copy.linkedArticles.searchPlaceholder}</span>
                        <input
                          type="search"
                          value={articleSearch}
                          onChange={event => setArticleSearch(event.target.value)}
                          placeholder={copy.linkedArticles.searchPlaceholder}
                          className="ticket-editor-input w-full rounded-[22px] border border-white/80 bg-white px-4 py-3 text-sm text-[#334155] outline-none shadow-[0_12px_28px_rgba(15,23,42,0.05)] transition placeholder:text-[#98a2b3] focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[var(--brand-primary-ring)]"
                        />
                      </label>

                      {matchingArticles.length > 0 ? (
                        <div className="ticket-linked-results overflow-hidden rounded-[24px] border border-[#e4e7ec] bg-white shadow-[0_16px_32px_rgba(15,23,42,0.04)]">
                          <div className="border-b border-[#eaecf0] px-4 py-3">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#98a2b3]">{copy.linkedArticles.searchResults}</p>
                          </div>
                          <div className="divide-y divide-[#eaecf0]">
                            {matchingArticles.map(article => (
                              <article key={article.id} className="flex flex-wrap items-start justify-between gap-3 px-4 py-4">
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-semibold text-[#101828]">{article.title}</p>
                                  <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#98a2b3]">{knowledgeArticleStatusLabel(article.status, language)} · {article.estimatedReadTime}</p>
                                  <p className="mt-2 line-clamp-2 text-sm leading-6 text-[#667085]">{article.summary || copy.linkedArticles.noSummary}</p>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => {
                                    onUpdateLinkedArticles(ticket.id, [...linkedArticleIds, article.id]);
                                    setArticleSearch("");
                                  }}
                                  className="brand-primary-button rounded-xl px-3 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-white transition-all"
                                >
                                  {copy.linkedArticles.attach}
                                </button>
                              </article>
                            ))}
                          </div>
                        </div>
                      ) : normalizedArticleSearch ? (
                        <div className="ticket-linked-empty rounded-[22px] border border-dashed border-[#d0d5dd] bg-white px-5 py-6 text-center">
                          <p className="text-sm font-semibold text-[#101828]">{copy.linkedArticles.noMatchesTitle}</p>
                          <p className="mt-2 text-sm text-[#667085]">{copy.linkedArticles.noMatchesDescription}</p>
                        </div>
                      ) : searchableArticles.some(article => !linkedArticleIds.includes(article.id)) ? null : (
                        <div className="ticket-linked-empty rounded-[22px] border border-dashed border-[#d0d5dd] bg-white px-5 py-6 text-center">
                          <p className="text-sm font-semibold text-[#101828]">{copy.linkedArticles.allLinkedTitle}</p>
                          <p className="mt-2 text-sm text-[#667085]">{copy.linkedArticles.allLinkedDescription}</p>
                        </div>
                      )}
                    </div>
                  ) : null}

                  <div className={`${canEditTickets && linkedArticles.length > 0 ? "mt-5" : "mt-0"} space-y-3`}>
                    {linkedArticles.map(article => (
                      <article key={article.id} className="ticket-linked-card rounded-[24px] border border-[#e4e7ec] bg-white p-5 shadow-[0_16px_32px_rgba(15,23,42,0.04)]">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <button
                              type="button"
                              onClick={() => setSelectedArticleModal(article)}
                              className="text-left text-sm font-bold text-[#101828] transition-colors hover:text-[var(--brand-primary)]"
                            >
                              {article.title}
                            </button>
                            <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#98a2b3]">
                              {knowledgeArticleStatusLabel(article.status, language)} · {article.estimatedReadTime}
                            </p>
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            <button
                              type="button"
                              aria-label={copy.linkedArticles.openLabel(article.title)}
                              title={copy.linkedArticles.openLabel(article.title)}
                              onClick={() => setSelectedArticleModal(article)}
                              className="ticket-linked-open-button inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white text-[#475467] shadow-[0_8px_20px_rgba(15,23,42,0.04)] transition-colors hover:bg-[#f8fafc]"
                            >
                              <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-4 w-4" xmlns="http://www.w3.org/2000/svg">
                                <path d="M2.917 10s2.917-4.583 7.083-4.583S17.083 10 17.083 10s-2.917 4.583-7.083 4.583S2.917 10 2.917 10Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M10 11.875A1.875 1.875 0 1 0 10 8.125a1.875 1.875 0 0 0 0 3.75Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                              <span className="sr-only">{copy.linkedArticles.openLabel(article.title)}</span>
                            </button>
                            {canEditTickets ? (
                              <button
                                type="button"
                                aria-label={copy.linkedArticles.removeLabel(article.title)}
                                title={copy.linkedArticles.removeLabel(article.title)}
                                onClick={() => onUpdateLinkedArticles(ticket.id, linkedArticleIds.filter(articleId => articleId !== article.id))}
                                className="ticket-linked-remove inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#fff1f3] text-[#c01048] transition-colors hover:bg-[#ffe4e8]"
                              >
                                <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-4 w-4" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M5.833 7.5v6.25A1.667 1.667 0 0 0 7.5 15.417h5a1.667 1.667 0 0 0 1.667-1.667V7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                  <path d="M4.167 5.417h11.666" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                  <path d="M7.917 5.417V4.583c0-.46.373-.833.833-.833h2.5c.46 0 .833.373.833.833v.834" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                  <path d="M8.75 9.583v3.334" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                  <path d="M11.25 9.583v3.334" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                </svg>
                                <span className="sr-only">{copy.linkedArticles.removeLabel(article.title)}</span>
                              </button>
                            ) : null}
                          </div>
                        </div>
                      </article>
                    ))}

                    {linkedArticleIds.length > linkedArticles.length ? (
                      <p className="text-xs text-[#98a2b3]">{copy.linkedArticles.unavailableNotice}</p>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="ticket-attachments-surface mt-5 overflow-hidden rounded-[28px] border border-[#e4e7ec] bg-[linear-gradient(180deg,#ffffff,#fcfcfd)] shadow-[0_16px_32px_rgba(15,23,42,0.04)]">
                <div className="flex flex-wrap items-center justify-between gap-3 px-6 pb-3 pt-6">
                  <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">{copy.attachments.title}</p>
                  <span className="ticket-attachments-badge rounded-full bg-white px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#475467] shadow-[0_8px_20px_rgba(15,23,42,0.04)]">
                    {copy.attachments.filesCount(ticketAttachments.length)}
                  </span>
                </div>

                <div className="px-6 pb-6 pt-1">
                  {canEditTickets ? (
                    <div className="flex flex-wrap items-center gap-3">
                      <label className="ticket-attachments-upload-button brand-primary-button inline-flex cursor-pointer items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-[11px] font-bold uppercase tracking-[0.18em] text-white transition-all">
                        <span>{isUploadingAttachments ? copy.attachments.uploading : copy.attachments.selectFiles}</span>
                        {isUploadingAttachments && uploadProgress !== null ? (
                          <span
                            className="inline-flex items-center"
                            role="progressbar"
                            aria-valuemin={0}
                            aria-valuemax={100}
                            aria-valuenow={uploadProgress}
                            aria-label={copy.attachments.uploadProgressLabel(uploadProgress)}
                          >
                            <svg viewBox="0 0 36 36" className="h-4 w-4 -rotate-90" xmlns="http://www.w3.org/2000/svg">
                              <circle cx="18" cy="18" r="16" fill="none" stroke="currentColor" strokeOpacity="0.3" strokeWidth="4" />
                              <circle
                                cx="18"
                                cy="18"
                                r="16"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="4"
                                strokeLinecap="round"
                                strokeDasharray={2 * Math.PI * 16}
                                strokeDashoffset={(1 - uploadProgress / 100) * 2 * Math.PI * 16}
                                style={{ transition: "stroke-dashoffset 0.2s ease" }}
                              />
                            </svg>
                          </span>
                        ) : null}
                        <input
                          type="file"
                          multiple
                          className="sr-only"
                          onChange={async event => {
                            const files = event.target.files;

                            if (!files || files.length === 0) {
                              return;
                            }

                            const selectedFiles = Array.from(files).filter(file => file.size > 0);

                            if (selectedFiles.length === 0) {
                              event.target.value = "";
                              return;
                            }

                            const existingBytes = ticketAttachments.reduce(
                              (total: number, attachment: TicketAttachment) => total + (Number.isFinite(attachment.size) ? attachment.size : 0),
                              0,
                            );
                            const incomingBytes = selectedFiles.reduce((total, file) => total + file.size, 0);

                            if (existingBytes + incomingBytes > maxTicketAttachmentTotalBytes) {
                              const limitMb = Math.round(maxTicketAttachmentTotalBytes / (1024 * 1024));
                              setAttachmentError(copy.attachments.sizeLimitError(limitMb));
                              event.target.value = "";
                              return;
                            }

                            setIsUploadingAttachments(true);
                            setUploadProgress(0);
                            setAttachmentError(null);

                            try {
                              await onUploadAttachments(ticket.id, selectedFiles, percent => setUploadProgress(percent));
                            } catch (error) {
                              setAttachmentError(error instanceof Error ? error.message : copy.attachments.uploadError);
                            } finally {
                              setIsUploadingAttachments(false);
                              setUploadProgress(null);
                              event.target.value = "";
                            }
                          }}
                        />
                      </label>
                      <p className="ticket-attachments-hint text-sm text-[#667085]">{copy.attachments.hint}</p>
                      <p className="ticket-attachments-hint mt-1 text-xs text-[#98a2b3]">{copy.attachments.sizeLimitHint}</p>
                    </div>
                  ) : null}

                  {attachmentError ? <p className="ticket-attachments-error mt-3 text-sm font-semibold text-[#b42318]">{attachmentError}</p> : null}

                  {isLoadingAttachments && ticketAttachments.length === 0 ? (
                    <div className={`${canEditTickets ? "mt-5" : "mt-0"} flex items-center gap-3 rounded-[24px] border border-dashed border-[#d0d5dd] bg-[#f8fafc] px-5 py-6 text-sm text-[#667085]`}>
                      <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white text-[var(--brand-primary)] shadow-[0_8px_20px_rgba(15,23,42,0.06)]" aria-hidden="true">
                        <svg viewBox="0 0 20 20" fill="none" className="h-4 w-4 animate-spin" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="10" cy="10" r="7" stroke="currentColor" strokeOpacity="0.2" strokeWidth="2" />
                          <path d="M10 3a7 7 0 0 1 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                      </span>
                      <span>{copy.attachments.loading}</span>
                    </div>
                  ) : ticketAttachments.length > 0 ? (
                    <div className={`${canEditTickets ? "mt-5" : "mt-0"} space-y-3`}>
                    {ticketAttachments.map((attachment: TicketAttachment) => {
                      const isDeletingAttachment = deletingAttachmentKeys.includes(attachment.key);
                      const isUpdatingShare = updatingShareKeys.includes(attachment.key);
                      const shareSelectValue: "default" | TicketAttachmentPublicShare = attachment.publicShareOverride ?? "default";

                      return (
                      <article key={attachment.key} className="ticket-attachments-card rounded-[24px] border border-[#e4e7ec] bg-white p-5 shadow-[0_16px_32px_rgba(15,23,42,0.04)]">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="truncate text-sm font-semibold text-[#101828]">{attachment.name}</p>
                              {attachment.publicDownloadEnabled ? (
                                <span
                                  title={copy.attachments.publicHint}
                                  className="inline-flex items-center gap-1 rounded-full bg-[#ecfdf3] px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] text-[#067647]"
                                >
                                  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-3 w-3" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="1.5" />
                                    <path d="M3 10h14M10 3c1.8 1.9 2.8 4.4 2.8 7s-1 5.1-2.8 7c-1.8-1.9-2.8-4.4-2.8-7S8.2 4.9 10 3Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
                                  </svg>
                                  {copy.attachments.publicBadge}
                                </span>
                              ) : null}
                            </div>
                            <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#98a2b3]">
                              {formatAttachmentSize(attachment.size)} · {formatAttachmentTimestamp(attachment.uploadedAt, language)}
                            </p>
                          </div>

                          <div className="flex flex-wrap items-center gap-2">
                            <button
                              type="button"
                              aria-label={copy.attachments.previewLabel(attachment.name)}
                              title={copy.attachments.previewLabel(attachment.name)}
                              onClick={() => setSelectedAttachmentModal(attachment)}
                              className="ticket-attachments-action inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white text-[#475467] shadow-[0_8px_20px_rgba(15,23,42,0.04)] transition-colors hover:bg-[#f8fafc]"
                            >
                              <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-4 w-4" xmlns="http://www.w3.org/2000/svg">
                                <path d="M2.917 10s2.917-4.583 7.083-4.583S17.083 10 17.083 10s-2.917 4.583-7.083 4.583S2.917 10 2.917 10Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M10 11.875A1.875 1.875 0 1 0 10 8.125a1.875 1.875 0 0 0 0 3.75Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                              <span className="sr-only">{copy.attachments.previewLabel(attachment.name)}</span>
                            </button>
                            <a
                              href={attachment.downloadUrl}
                              aria-label={copy.attachments.downloadLabel(attachment.name)}
                              title={copy.attachments.downloadLabel(attachment.name)}
                              className="ticket-attachments-action inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white text-[#475467] shadow-[0_8px_20px_rgba(15,23,42,0.04)] transition-colors hover:bg-[#f8fafc]"
                            >
                              <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-4 w-4" xmlns="http://www.w3.org/2000/svg">
                                <path d="M10 3.75v7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M6.875 8.958 10 12.083l3.125-3.125" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M4.583 13.75v.417c0 .69.56 1.25 1.25 1.25h8.334c.69 0 1.25-.56 1.25-1.25v-.417" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                              <span className="sr-only">{copy.attachments.downloadLabel(attachment.name)}</span>
                            </a>
                            <button
                              type="button"
                              aria-label={copy.attachments.copyLinkLabel(attachment.name)}
                              title={copiedAttachmentKey === attachment.key ? copy.attachments.linkCopied : copy.attachments.copyLinkLabel(attachment.name)}
                              onClick={() => void handleCopyAttachmentLink(attachment)}
                              className={`ticket-attachments-action inline-flex h-10 w-10 items-center justify-center rounded-xl shadow-[0_8px_20px_rgba(15,23,42,0.04)] transition-colors ${copiedAttachmentKey === attachment.key ? "bg-[#ecfdf3] text-[#067647]" : "bg-white text-[#475467] hover:bg-[#f8fafc]"}`}
                            >
                              {copiedAttachmentKey === attachment.key ? (
                                <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-4 w-4" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M5 10.417 8.333 13.75 15 6.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              ) : (
                                <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-4 w-4" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M8.333 10.833a2.5 2.5 0 0 0 3.767.27l1.667-1.666a2.5 2.5 0 0 0-3.536-3.536l-.955.95" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                  <path d="M11.667 9.167a2.5 2.5 0 0 0-3.767-.27L6.233 10.563a2.5 2.5 0 0 0 3.536 3.536l.95-.95" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              )}
                              <span className="sr-only">{copiedAttachmentKey === attachment.key ? copy.attachments.linkCopied : copy.attachments.copyLinkLabel(attachment.name)}</span>
                            </button>
                            {attachment.publicDownloadEnabled && attachment.publicDownloadUrl ? (
                              <button
                                type="button"
                                aria-label={copy.attachments.copyPublicLinkLabel(attachment.name)}
                                title={copiedPublicAttachmentKey === attachment.key ? copy.attachments.linkCopied : copy.attachments.copyPublicLinkLabel(attachment.name)}
                                onClick={() => void handleCopyPublicAttachmentLink(attachment)}
                                className={`ticket-attachments-action inline-flex h-10 w-10 items-center justify-center rounded-xl shadow-[0_8px_20px_rgba(15,23,42,0.04)] transition-colors ${copiedPublicAttachmentKey === attachment.key ? "bg-[#ecfdf3] text-[#067647]" : "bg-white text-[#475467] hover:bg-[#f8fafc]"}`}
                              >
                                {copiedPublicAttachmentKey === attachment.key ? (
                                  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-4 w-4" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M5 10.417 8.333 13.75 15 6.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                ) : (
                                  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-4 w-4" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="1.5" />
                                    <path d="M3 10h14M10 3c1.8 1.9 2.8 4.4 2.8 7s-1 5.1-2.8 7c-1.8-1.9-2.8-4.4-2.8-7S8.2 4.9 10 3Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
                                  </svg>
                                )}
                                <span className="sr-only">{copy.attachments.copyPublicLinkLabel(attachment.name)}</span>
                              </button>
                            ) : null}
                            {canEditTickets ? (
                              <button
                                type="button"
                                disabled={isDeletingAttachment}
                                aria-busy={isDeletingAttachment}
                                aria-label={copy.attachments.removeLabel(attachment.name)}
                                title={copy.attachments.removeLabel(attachment.name)}
                                onClick={async () => {
                                  setAttachmentError(null);
                                  setDeletingAttachmentKeys(current => (current.includes(attachment.key) ? current : [...current, attachment.key]));

                                  try {
                                    await onDeleteAttachment(ticket.id, attachment.key);
                                  } catch (error) {
                                    setAttachmentError(error instanceof Error ? error.message : copy.attachments.removeError);
                                  } finally {
                                    setDeletingAttachmentKeys(current => current.filter(key => key !== attachment.key));
                                  }
                                }}
                                className={`ticket-attachments-remove inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#fff1f3] text-[#c01048] transition-colors ${isDeletingAttachment ? "cursor-wait opacity-70" : "hover:bg-[#ffe4e8]"}`}
                              >
                                {isDeletingAttachment ? (
                                  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-4 w-4 animate-spin" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M10 3.75a6.25 6.25 0 1 1-4.419 1.831" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" opacity="0.35" />
                                    <path d="M10 3.75a6.25 6.25 0 0 1 4.419 1.831" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
                                  </svg>
                                ) : (
                                  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-4 w-4" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M5.833 7.5v6.25A1.667 1.667 0 0 0 7.5 15.417h5a1.667 1.667 0 0 0 1.667-1.667V7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M4.167 5.417h11.666" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                    <path d="M7.917 5.417V4.583c0-.46.373-.833.833-.833h2.5c.46 0 .833.373.833.833v.834" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M8.75 9.583v3.334" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                    <path d="M11.25 9.583v3.334" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                  </svg>
                                )}
                                <span className="sr-only">{copy.attachments.removeLabel(attachment.name)}</span>
                              </button>
                            ) : null}
                          </div>
                        </div>
                        {attachmentOverridesEnabled && canEditTickets ? (
                          <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-[#f2f4f7] pt-4">
                            <label htmlFor={`attachment-share-${attachment.key}`} className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#98a2b3]">
                              {copy.attachments.shareLabel}
                            </label>
                            <select
                              id={`attachment-share-${attachment.key}`}
                              aria-label={copy.attachments.shareAria(attachment.name)}
                              disabled={isUpdatingShare}
                              value={shareSelectValue}
                              onChange={event => {
                                const nextValue = event.target.value === "default" ? null : (event.target.value as TicketAttachmentPublicShare);
                                void handleAttachmentShareChange(attachment, nextValue);
                              }}
                              className={`rounded-xl border border-[#e4e7ec] bg-white px-3 py-1.5 text-xs font-semibold text-[#344054] shadow-[0_8px_20px_rgba(15,23,42,0.04)] transition-colors focus:border-[#7f56d9] focus:outline-none ${isUpdatingShare ? "cursor-wait opacity-70" : "hover:border-[#cdd2db]"}`}
                            >
                              <option value="default">{copy.attachments.shareOptionDefault(publicAttachmentDefaultStateLabel)}</option>
                              <option value="allow">{copy.attachments.shareOptionAllow}</option>
                              <option value="deny">{copy.attachments.shareOptionDeny}</option>
                            </select>
                          </div>
                        ) : null}
                      </article>
                    );})}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </section>

          <section className="rounded-[32px] border border-white/60 bg-white p-6 shadow-[0_20px_40px_rgba(26,28,29,0.04)]">
            <div className="rounded-[28px]">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">{copy.notes.title}</p>
              </div>

              {canEditTickets ? <div className={`ticket-note-composer mt-5 overflow-visible rounded-[26px] border transition-all ${noteComposerTheme.surfaceClassName}`}>
                <div className={`flex items-center justify-between gap-3 border-b px-5 py-4 ${noteComposerTheme.headerClassName}`}>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-[#98a2b3]">{copy.notes.addNote}</p>
                    <p className="mt-1 text-sm text-[#667085]">{copy.notes.helper}</p>
                  </div>
                  <UserAvatarBadge
                    avatarUrl={currentWorkspaceMember?.avatarUrl ?? null}
                    initials={currentWorkspaceMember?.initials ?? getInitials(currentUser)}
                    className="brand-primary-avatar flex h-11 w-11 items-center justify-center rounded-2xl text-sm font-bold shadow-[0_10px_24px_rgba(0,105,73,0.16)]"
                  />
                </div>

                <div className="p-5">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex flex-wrap gap-2">
                      {(["internal", "external"] as const).map(kind => {
                        const isSelected = noteKind === kind;

                        return (
                          <button
                            key={kind}
                            type="button"
                            onClick={() => setNoteKind(kind)}
                            className={`ticket-note-toggle rounded-full px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] transition-all ${isSelected ? getTicketNoteComposerTheme(kind, copy).selectedOptionClassName : "ticket-note-toggle--idle"}`}
                          >
                            {copy.notes.kinds[kind]}
                          </button>
                        );
                      })}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        void handleSubmitNote();
                      }}
                      disabled={!hasPendingNote || Boolean(noteEditorError) || !isNoteEditorReady}
                      className={`rounded-xl px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] transition-all ${
                        hasPendingNote && !noteEditorError && isNoteEditorReady
                            ? noteComposerTheme.submitButtonClassName
                          : "cursor-not-allowed bg-white text-slate-300 shadow-[0_8px_20px_rgba(15,23,42,0.04)]"
                      }`}
                    >
                      {noteKind === "external" ? copy.notes.submit.external : copy.notes.submit.internal}
                    </button>
                  </div>
                  <QuillEditor
                    ref={noteEditorRef}
                    preset="note"
                    placeholder={noteKind === "external" ? copy.notes.placeholders.external : copy.notes.placeholders.internal}
                    className="quill-host quill-host--note ticket-note-editor-host ticket-note-editor-shell relative z-[2] mt-4 min-h-[13rem]"
                    onChange={handleNoteEditorChange}
                    onReady={() => {
                      setIsNoteEditorReady(true);
                      setNoteEditorError(null);
                    }}
                    onError={() => {
                      setIsNoteEditorReady(false);
                      setNoteEditorError(copy.notes.editorFailed);
                    }}
                  />
                  <div className="mt-3 space-y-1 text-xs text-[#667085]">
                    {noteEditorError ? <p className="font-semibold text-[#b42318]">{noteEditorError}</p> : null}
                  </div>
                </div>
              </div> : null}

              <div className="mt-5 space-y-4">
                {caseNotes.length > 0 ? (
                  caseNotes.map(note => (
                    <article
                      key={note.id}
                      className={`ticket-note-entry overflow-hidden rounded-[24px] border p-5 shadow-[0_16px_36px_rgba(15,23,42,0.06)] ${getTicketNoteAppearance(note, copy).cardClassName}`}
                    >
                      {(() => {
                        const noteAuthorMember = resolveWorkspaceMember(teamMembers, note.author);
                        const noteTheme = getTicketNoteAppearance(note, copy);

                        return (
                      <div className="flex flex-wrap items-start justify-between gap-4">
                        <div className="flex min-w-0 items-start gap-3">
                          <UserAvatarBadge
                            avatarUrl={noteAuthorMember?.avatarUrl ?? null}
                            initials={noteAuthorMember?.initials ?? (getInitials(note.author || copy.notes.existingCaseNote) || copy.notes.importedInitials)}
                            className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl text-sm font-bold ${noteTheme.avatarClassName}`}
                          />
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="text-sm font-bold text-[#101828]">{note.author}</p>
                              <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] ${noteTheme.kindBadgeClassName}`}>
                                {noteTheme.kindLabel}
                              </span>
                            </div>
                            <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#98a2b3]">
                              {formatTicketNoteTimestamp(note, timezone, copy)}
                            </p>
                          </div>
                        </div>

                        {noteTheme.metaBadgeClassName && noteTheme.metaBadgeLabel ? <span className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] ${noteTheme.metaBadgeClassName}`}>{noteTheme.metaBadgeLabel}</span> : null}
                      </div>
                        );
                      })()}

                      <div className={`ticket-note-body mt-4 rounded-[20px] px-4 py-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.6)] ${getTicketNoteAppearance(note, copy).bodyClassName}`}>
                        {note.bodyContent?.html ? (
                          <RichTextContentRenderer content={note.bodyContent} variant="note" />
                        ) : (
                          <p className="whitespace-pre-line text-sm leading-7 text-[#475467]">{note.body}</p>
                        )}
                      </div>
                    </article>
                  ))
                ) : (
                  <div className="rounded-[24px] border border-dashed border-[#d0d5dd] bg-white px-5 py-8 text-center shadow-[0_16px_32px_rgba(15,23,42,0.04)]">
                    <p className="text-sm font-semibold text-[#101828]">{copy.notes.emptyTitle}</p>
                    <p className="mt-2 text-sm text-[#667085]">{copy.notes.emptyDescription}</p>
                  </div>
                )}
              </div>
            </div>
          </section>

        </div>

        <aside className="space-y-6">
          <section className="rounded-[32px] border border-white/60 bg-white p-6 shadow-[0_20px_40px_rgba(26,28,29,0.04)]">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">{copy.details.title}</p>            </div>

            <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-1 2xl:grid-cols-2">
              <div className="rounded-[24px] bg-[#f9f9fa] p-3">
                <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">{copy.details.category}</p>
                <div className="mt-1">
                  {canEditTickets ? (
                    <TicketPillMenu
                      value={ticket.category}
                      options={ticketCategoryNames}
                      ariaLabel={copy.details.changeCategory}
                      onChange={category => onUpdateCategory(ticket.id, category)}
                      buttonClassName="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-[10px] font-bold uppercase tracking-wide shadow-[0_8px_20px_rgba(15,23,42,0.05)] text-[#475467]"
                      renderValue={category => <span>{category}</span>}
                      renderOption={category => <span>{category}</span>}
                    />
                  ) : <span className="inline-flex items-center rounded-full bg-white px-3 py-1 text-[10px] font-bold uppercase tracking-wide shadow-[0_8px_20px_rgba(15,23,42,0.05)] text-[#475467]">{ticket.category}</span>}
                </div>
              </div>

              {ticket.sla ? (
                <div className="rounded-[24px] bg-[#f9f9fa] p-3">
                  <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">{copy.details.sla}</p>
                  <p className="mt-1 text-sm font-semibold text-[#101828]">{formatTicketSlaLabel(ticket.sla)}</p>
                  {slaCountdown ? (
                    <p className={`mt-1 text-xs font-semibold ${slaCountdown.isPaused ? "text-[#c2410c]" : slaCountdown.isOverdue ? "text-[#b42318]" : "text-[#175cd3]"}`}>
                      {slaCountdown.isPaused ? copy.details.pausedRemaining(slaCountdown.displayLabel) : slaCountdown.isOverdue ? copy.details.overSla(slaCountdown.displayLabel) : copy.details.untilBreach(slaCountdown.displayLabel)}
                    </p>
                  ) : null}
                </div>
              ) : null}

              {canEditTickets ? (
                <button
                  type="button"
                  onClick={() => setIsScheduleModalOpen(true)}
                  className="rounded-[24px] bg-[#f9f9fa] p-3 text-left transition-colors hover:bg-[#f3f4f6] focus:outline-none focus:ring-2 focus:ring-[#006949]/15"
                  aria-haspopup="dialog"
                  aria-expanded={isScheduleModalOpen}
                  aria-label={copy.details.openScheduleEditor}
                >
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">{copy.details.schedule}</p>
                    <p className="mt-1 text-sm font-semibold text-[#101828]">{formatScheduleRangeLabel(ticket.scheduleStartAt, ticket.scheduleAt, copy.details.noScheduleSet, timezone)}</p>
                  </div>
                </button>
              ) : (
                <div className="rounded-[24px] bg-[#f9f9fa] p-3 text-left">
                  <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">{copy.details.schedule}</p>
                  <p className="mt-1 text-sm font-semibold text-[#101828]">{formatScheduleRangeLabel(ticket.scheduleStartAt, ticket.scheduleAt, copy.details.noScheduleSet, timezone)}</p>
                </div>
              )}

              <div className="rounded-[24px] bg-[#f9f9fa] p-3">
                <span className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">{copy.details.duration}</span>
                <p className="mt-1 text-sm font-semibold leading-5 text-[#101828]">{copy.details.open} <TicketDurationText value={ticketCreatedAt} /></p>
                <p className="mt-1 text-[11px] text-[#667085]">{copy.details.created(formatPreciseDateTimeLabel(ticketCreatedAt, copy.details.unknown, timezone))}</p>
              </div>

              <div className="rounded-[24px] bg-[#f9f9fa] p-3">
                <span className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">{copy.details.lastUpdated}</span>
                <TicketLastUpdateText value={ticket.lastUpdate} timezone={timezone} as="button" className="mt-1 w-full justify-start text-left text-sm font-semibold leading-5 text-[#101828]" />
              </div>
            </div>

            {ticketCustomFields.length > 0 ? (
              <div className="mt-4 border-t border-[#eaecf0] pt-4">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">{copy.details.categoryFields}</p>
                  {canEditTickets ? (
                    <button
                      type="button"
                      onClick={() => {
                        const nextValues = syncTicketCustomFieldValues(ticketCustomFields, customFieldDraft);
                        const nextError = getTicketDetailCustomFieldError(ticketCustomFields, nextValues, copy);

                        if (nextError) {
                          setCustomFieldError(nextError);
                          return;
                        }

                        onUpdateCustomFieldValues(ticket.id, nextValues);
                        setCustomFieldError(null);
                      }}
                      className={`inline-flex items-center justify-center rounded-xl border px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] shadow-[0_10px_24px_rgba(15,23,42,0.08)] transition-all focus:outline-none focus:ring-2 focus:ring-[#006949]/15 ${
                        hasPendingCustomFields
                          ? "brand-primary-button border-transparent text-white hover:brightness-[1.03]"
                          : "border-[#d0d5dd] bg-[#f9fafb] text-[#344054] hover:border-[#98a2b3] hover:bg-white"
                      }`}
                    >
                      {copy.details.saveFields}
                    </button>
                  ) : null}
                </div>

                <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-1 2xl:grid-cols-2">
                  {ticketCustomFields.map(field => (
                    <label key={field.id} className={`rounded-[24px] bg-[#f9f9fa] p-3 ${field.type === "textarea" ? "sm:col-span-2 lg:col-span-1 2xl:col-span-2" : ""}`}>
                      <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">
                        {field.label}
                        {field.required ? " *" : ""}
                      </span>
                      {field.type === "textarea" ? (
                        <textarea
                          value={customFieldDraft[field.id] ?? ""}
                          onChange={event => {
                            const nextValue = event.target.value;
                            setCustomFieldDraft(current => ({ ...current, [field.id]: nextValue }));
                            setCustomFieldError(null);
                          }}
                          disabled={!canEditTickets}
                          rows={4}
                          className="ticket-editor-input mt-2 w-full rounded-[18px] border border-white/80 bg-white px-4 py-4 text-sm leading-7 text-[#334155] outline-none shadow-[0_12px_28px_rgba(15,23,42,0.05)] transition focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[var(--brand-primary-ring)] disabled:bg-[#f8fafc] disabled:text-[#667085]"
                        />
                      ) : field.type === "select" ? (
                        <select
                          value={customFieldDraft[field.id] ?? ""}
                          onChange={event => {
                            const nextValue = event.target.value;
                            setCustomFieldDraft(current => ({ ...current, [field.id]: nextValue }));
                            setCustomFieldError(null);
                          }}
                          disabled={!canEditTickets}
                          className="ticket-editor-input mt-2 w-full rounded-[18px] border border-white/80 bg-white px-4 py-3 text-sm text-[#334155] outline-none shadow-[0_12px_28px_rgba(15,23,42,0.05)] transition focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[var(--brand-primary-ring)] disabled:bg-[#f8fafc] disabled:text-[#667085]"
                        >
                          <option value="">{copy.details.selectField(field.label)}</option>
                          {field.options.map(option => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type={field.type === "date" ? "date" : "text"}
                          value={customFieldDraft[field.id] ?? ""}
                          onChange={event => {
                            const nextValue = event.target.value;
                            setCustomFieldDraft(current => ({ ...current, [field.id]: nextValue }));
                            setCustomFieldError(null);
                          }}
                          disabled={!canEditTickets}
                          className="ticket-editor-input mt-2 w-full rounded-[18px] border border-white/80 bg-white px-4 py-3 text-sm text-[#334155] outline-none shadow-[0_12px_28px_rgba(15,23,42,0.05)] transition focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[var(--brand-primary-ring)] disabled:bg-[#f8fafc] disabled:text-[#667085]"
                        />
                      )}
                      {field.helpText ? <p className="mt-2 text-xs text-[#667085]">{field.helpText}</p> : null}
                    </label>
                  ))}
                </div>

                {customFieldError ? <p className="mt-4 text-sm font-semibold text-[#b42318]">{customFieldError}</p> : null}
              </div>
            ) : null}
          </section>

          <section className="rounded-[32px] border border-white/60 bg-white p-6 shadow-[0_20px_40px_rgba(26,28,29,0.04)]">
            {hasCustomer ? (
              <>
                <div className="flex items-start gap-4">
                  <div className="brand-primary-avatar flex h-16 w-16 items-center justify-center rounded-full text-lg font-bold">{customer.initials}</div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">{copy.customer.title}</p>
                    <h2 className="mt-2 font-headline text-2xl font-extrabold tracking-[-0.04em] text-[#101828]">{customer.name}</h2>
                    <p className="mt-1 text-sm text-[#667085]">{customer.company}</p>
                  </div>
                </div>

                <div className="mt-5 flex flex-wrap gap-2">
                  <span className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wide ${healthClasses[customer.health]}`}>{customer.health}</span>
                </div>

                <div className="mt-6 grid gap-4 text-sm">
                  <div>
                    <p className="text-slate-400">{copy.customer.email}</p>
                    <p className="mt-1 font-semibold text-[#101828]">{customer.email}</p>
                  </div>
                  <div>
                    <p className="text-slate-400">{copy.customer.phone}</p>
                    <p className="mt-1 font-semibold text-[#101828]">{customer.phone}</p>
                  </div>
                  <div>
                    <p className="text-slate-400">{copy.customer.region}</p>
                    <p className="mt-1 font-semibold text-[#101828]">{customer.region}</p>
                  </div>
                  <div>
                    <p className="text-slate-400">{copy.customer.renewalDate}</p>
                    <p className="mt-1 font-semibold text-[#101828]">{customer.renewalDate}</p>
                  </div>
                </div>

                <div className={`mt-6 grid gap-3 ${canEditTickets ? "sm:grid-cols-2" : ""}`}>
                  <button
                    type="button"
                    onClick={() => onOpenCustomer(customer.id)}
                    className="brand-primary-button w-full rounded-xl px-5 py-3 text-sm font-semibold transition-all"
                  >
                    {copy.customer.openProfile}
                  </button>
                  {canEditTickets ? (
                    <button
                      type="button"
                      onClick={openCustomerModal}
                      className="w-full rounded-xl border border-[#d0d5dd] bg-white px-5 py-3 text-sm font-semibold text-[#344054] shadow-[0_10px_20px_rgba(15,23,42,0.05)] transition hover:border-[#98a2b3] hover:bg-[#f8fafc]"
                    >
                      {copy.customer.changeCustomer}
                    </button>
                  ) : null}
                </div>
              </>
            ) : (
              <div className="rounded-[24px] border border-dashed border-[#d0d5dd] bg-[#fcfcfd] px-5 py-8 text-center">
                <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">{copy.customer.title}</p>
                <p className="mt-3 text-lg font-semibold text-[#101828]">{copy.none}</p>
                <p className="mt-2 text-sm text-[#667085]">{copy.customer.noCustomerProfile}</p>
                {canEditTickets ? (
                  <button
                    type="button"
                    onClick={openCustomerModal}
                    className="brand-primary-button mt-6 w-full rounded-xl px-5 py-3 text-sm font-semibold transition-all"
                  >
                    {copy.customer.addCustomer}
                  </button>
                ) : null}
              </div>
            )}
          </section>

          <section className="rounded-[32px] border border-white/60 bg-white p-6 shadow-[0_20px_40px_rgba(26,28,29,0.04)]">
            <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">{copy.relatedTickets.title}</p>
            <div className="mt-5 space-y-3">
              {relatedTickets.length > 0 ? (
                relatedTicketsPreview.map(item => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => onOpenTicket(item.id)}
                    className="related-ticket-card w-full rounded-[24px] border border-[rgba(0,0,0,0.04)] bg-[#fcfcfd] p-5 text-left transition-all hover:border-[rgba(0,105,73,0.14)] hover:bg-white hover:shadow-[0_16px_32px_rgba(15,23,42,0.06)] focus:outline-none focus:ring-2 focus:ring-[#006949]/15"
                    aria-label={copy.relatedTickets.openTicket(item.id)}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="flex flex-wrap items-center gap-2.5">
                        <p className="text-sm font-bold text-[#101828]">#{item.id}</p>
                        <span className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wide ${priorityClasses[item.priority]}`}>{copy.priorityLabels[item.priority]}</span>
                        <span className={`rounded-full bg-white px-3 py-1 text-[10px] font-bold uppercase tracking-wide shadow-[0_8px_20px_rgba(15,23,42,0.05)] ${statusClasses[item.status]}`}>{copy.statusLabels[item.status]}</span>
                        <span className="rounded-full bg-white px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-[#667085] shadow-[0_8px_20px_rgba(15,23,42,0.05)]">{item.category}</span>
                      </div>
                    </div>
                    <div className="mt-3">
                      <p className="text-sm font-medium leading-6 text-[#475467]">{item.subject}</p>
                    </div>
                  </button>
                ))
              ) : (
                <div className="rounded-[24px] border border-dashed border-[#d0d5dd] bg-[#fcfcfd] px-5 py-8 text-center">
                  <p className="text-sm font-semibold text-[#101828]">{copy.relatedTickets.emptyTitle}</p>
                  <p className="mt-2 text-sm text-[#667085]">{hasCustomer ? copy.relatedTickets.emptyWithCustomer : copy.relatedTickets.emptyWithoutCustomer}</p>
                </div>
              )}

              {relatedTickets.length > relatedTicketsPreview.length ? (
                <button
                  type="button"
                  onClick={() => setIsRelatedTicketsModalOpen(true)}
                  className="flex w-full items-center justify-between rounded-[24px] border border-dashed border-[#d0d5dd] bg-[#fcfcfd] px-5 py-4 text-left transition-colors hover:border-[#98a2b3] hover:bg-white"
                >
                  <div>
                    <p className="text-sm font-semibold text-[#101828]">{copy.relatedTickets.viewAllTitle}</p>
                    <p className="mt-1 text-sm text-[#667085]">{copy.relatedTickets.viewAllDescription(relatedTickets.length)}</p>
                  </div>
                  <span className="text-sm font-semibold text-[#006949]">{copy.relatedTickets.open}</span>
                </button>
              ) : null}
            </div>
          </section>

          <section className="rounded-[32px] border border-white/60 bg-white p-6 shadow-[0_20px_40px_rgba(26,28,29,0.04)]">
            <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">{copy.activity.title}</p>

            <div className="mt-5 space-y-4">
              {ticketActivities.length > 0 ? (
                activityPreview.map(activity => (
                  <div key={activity.id} className="flex gap-4 rounded-[24px] bg-[#f9f9fa] p-5">
                    <div className="mt-1 h-3 w-3 rounded-full bg-[#006949]" />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="text-sm font-semibold text-[#101828]">{activity.actor}</p>
                        <span className="text-[11px] font-medium uppercase tracking-[0.16em] text-[#98a2b3]">
                          {formatRelativeDateTimeLabel(activity.createdAt, activity.time, currentTime)}
                        </span>
                      </div>
                      <p className="mt-2 text-sm leading-6 text-[#475467]">{activity.detail}</p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="rounded-[24px] border border-dashed border-[#d0d5dd] bg-[#fcfcfd] px-5 py-8 text-center">
                  <p className="text-sm font-semibold text-[#101828]">{copy.activity.emptyTitle}</p>
                  <p className="mt-2 text-sm text-[#667085]">{copy.activity.emptyDescription}</p>
                </div>
              )}

              {ticketActivities.length > activityPreview.length ? (
                <button
                  type="button"
                  onClick={() => setIsActivityModalOpen(true)}
                  className="flex w-full items-center justify-between rounded-[24px] border border-dashed border-[#d0d5dd] bg-[#fcfcfd] px-5 py-4 text-left transition-colors hover:border-[#98a2b3] hover:bg-white"
                >
                  <div>
                    <p className="text-sm font-semibold text-[#101828]">{copy.activity.viewAllTitle}</p>
                    <p className="mt-1 text-sm text-[#667085]">{copy.activity.viewAllDescription(ticketActivities.length)}</p>
                  </div>
                  <span className="text-sm font-semibold text-[#006949]">{copy.activity.open}</span>
                </button>
              ) : null}
            </div>
          </section>
        </aside>
      </div>

      <AnimatedModal
        open={Boolean(isScheduleModalOpen && canEditTickets)}
        onClose={() => setIsScheduleModalOpen(false)}
        backdropAriaLabel={copy.scheduleModal.closeLabel}
        dialogLabelledBy="ticket-schedule-dialog-title"
        overlayClassName="fixed inset-0 z-[80] flex items-center justify-center bg-[#101828]/36 p-6 backdrop-blur-[4px]"
        dialogClassName="app-modal-dialog relative z-[1] w-full max-w-xl rounded-[32px] border border-white/70 bg-white p-8 shadow-[0_32px_64px_rgba(15,23,42,0.22)]"
      >
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">{copy.scheduleModal.title}</p>
                <h2 id="ticket-schedule-dialog-title" className="mt-2 font-headline text-3xl font-extrabold tracking-[-0.05em] text-[#101828]">
                  {copy.scheduleModal.heading}
                </h2>
                <p className="mt-2 text-sm text-[#667085]">{copy.scheduleModal.description}</p>
              </div>
              <button
                type="button"
                onClick={() => setIsScheduleModalOpen(false)}
                className="rounded-full bg-[#f3f4f6] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#475467] transition-colors hover:bg-[#e4e7ec]"
              >
                {copy.scheduleModal.close}
              </button>
            </div>

            <div className="mt-6 rounded-[24px] bg-[#f9f9fa] p-5">
              <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">{copy.scheduleModal.currentSchedule}</p>
              <p className="mt-2 text-sm font-semibold text-[#101828]">
                {formatScheduleRangeLabel(ticket.scheduleStartAt, ticket.scheduleAt, copy.details.noScheduleSet, timezone)}
              </p>
            </div>

            <label className="mt-6 block">
              <span className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">{copy.scheduleModal.startDateTime}</span>
              <input
                type="datetime-local"
                value={scheduleStartDraft}
                onChange={event => setScheduleStartDraft(event.target.value)}
                className="mt-3 w-full rounded-2xl border border-white/80 bg-white px-4 py-3 text-sm text-[#334155] outline-none shadow-[0_12px_28px_rgba(15,23,42,0.05)] transition focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[var(--brand-primary-ring)]"
              />
            </label>

            <label className="mt-6 block">
              <span className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">{copy.scheduleModal.dateTime}</span>
              <input
                type="datetime-local"
                value={scheduleDraft}
                onChange={event => setScheduleDraft(event.target.value)}
                className="mt-3 w-full rounded-2xl border border-white/80 bg-white px-4 py-3 text-sm text-[#334155] outline-none shadow-[0_12px_28px_rgba(15,23,42,0.05)] transition focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[var(--brand-primary-ring)]"
              />
              <p className="mt-2 text-xs text-[#667085]">{copy.scheduleModal.timezoneHelp(timezone)}</p>
            </label>

            <div className="mt-6 flex flex-wrap items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setScheduleStartDraft("");
                  setScheduleDraft("");
                  if (ticket.scheduleAt || ticket.scheduleStartAt) {
                    onUpdateSchedule(ticket.id, null, null);
                  }
                  setIsScheduleModalOpen(false);
                }}
                disabled={!ticket.scheduleAt && !ticket.scheduleStartAt && !scheduleDraft && !scheduleStartDraft}
                className={`rounded-xl px-4 py-3 text-[11px] font-bold uppercase tracking-[0.18em] transition-all ${
                  ticket.scheduleAt || ticket.scheduleStartAt || scheduleDraft || scheduleStartDraft
                    ? "bg-white text-[#475467] shadow-[0_8px_20px_rgba(15,23,42,0.04)] hover:bg-[#f8fafc]"
                    : "cursor-not-allowed bg-white text-slate-300 shadow-[0_8px_20px_rgba(15,23,42,0.04)]"
                }`}
              >
                {copy.scheduleModal.clear}
              </button>
              <button
                type="button"
                onClick={() => {
                  onUpdateSchedule(ticket.id, normalizeDateTimeForStorage(scheduleStartDraft, timezone), normalizeDateTimeForStorage(scheduleDraft, timezone));
                  setIsScheduleModalOpen(false);
                }}
                disabled={!hasPendingSchedule}
                className={`rounded-xl px-4 py-3 text-[11px] font-bold uppercase tracking-[0.18em] transition-all ${
                  hasPendingSchedule
                    ? "brand-primary-button text-white"
                    : "cursor-not-allowed bg-white text-slate-300 shadow-[0_8px_20px_rgba(15,23,42,0.04)]"
                }`}
              >
                {copy.scheduleModal.save}
              </button>
            </div>
      </AnimatedModal>

      <AnimatedModal
        open={isActivityModalOpen}
        onClose={() => setIsActivityModalOpen(false)}
        backdropAriaLabel={copy.activity.modalClose}
        dialogLabelledBy="ticket-activity-dialog-title"
        overlayClassName="fixed inset-0 z-[81] flex items-center justify-center bg-[#101828]/40 p-6 backdrop-blur-[4px]"
        dialogClassName="app-modal-dialog relative z-[1] flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-[32px] border border-white/70 bg-white shadow-[0_32px_64px_rgba(15,23,42,0.22)]"
      >
            <div className="flex items-start justify-between gap-4 border-b border-[#eaecf0] px-7 py-6">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">{copy.activity.title}</p>
                <h2 id="ticket-activity-dialog-title" className="mt-2 font-headline text-3xl font-extrabold tracking-[-0.05em] text-[#101828]">
                  {copy.activity.modalTitle}
                </h2>
                <p className="mt-2 text-sm text-[#667085]">{copy.activity.modalDescription(ticket.id)}</p>
              </div>
              <button
                type="button"
                onClick={() => setIsActivityModalOpen(false)}
                className="rounded-full bg-[#f3f4f6] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#475467] transition-colors hover:bg-[#e4e7ec]"
              >
                {copy.scheduleModal.close}
              </button>
            </div>

            <div className="custom-scrollbar overflow-y-auto px-7 py-6">
              <div className="space-y-4">
                {ticketActivities.map(activity => (
                  <div key={activity.id} className="flex gap-4 rounded-[24px] bg-[#f9f9fa] p-5">
                    <div className="mt-1 h-3 w-3 rounded-full bg-[#006949]" />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div>
                          <p className="text-sm font-semibold text-[#101828]">{activity.actor}</p>
                          <p className="mt-1 text-xs text-[#667085]">{formatPreciseDateTimeLabel(activity.createdAt, activity.time, timezone)}</p>
                        </div>
                        <span className="text-[11px] font-medium uppercase tracking-[0.16em] text-[#98a2b3]">
                          {formatRelativeDateTimeLabel(activity.createdAt, activity.time, currentTime)}
                        </span>
                      </div>
                      <p className="mt-2 text-sm leading-6 text-[#475467]">{activity.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
      </AnimatedModal>

      <AnimatedModal
        open={isRelatedTicketsModalOpen}
        onClose={() => setIsRelatedTicketsModalOpen(false)}
        backdropAriaLabel={copy.relatedTickets.modalClose}
        dialogLabelledBy="related-tickets-dialog-title"
        overlayClassName="fixed inset-0 z-[82] flex items-center justify-center bg-[#101828]/40 p-6 backdrop-blur-[4px]"
        dialogClassName="app-modal-dialog relative z-[1] flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-[32px] border border-white/70 bg-white shadow-[0_32px_64px_rgba(15,23,42,0.22)]"
      >
            <div className="flex items-start justify-between gap-4 border-b border-[#eaecf0] px-7 py-6">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">{copy.relatedTickets.title}</p>
                <h2 id="related-tickets-dialog-title" className="mt-2 font-headline text-3xl font-extrabold tracking-[-0.05em] text-[#101828]">
                  {hasCustomer ? copy.relatedTickets.modalTitleWithCustomer : copy.relatedTickets.modalTitleWithoutCustomer}
                </h2>
                <p className="mt-2 text-sm text-[#667085]">{hasCustomer ? copy.relatedTickets.modalDescriptionWithCustomer(customer.company) : copy.relatedTickets.modalDescriptionWithoutCustomer}</p>
              </div>
              <button
                type="button"
                onClick={() => setIsRelatedTicketsModalOpen(false)}
                className="rounded-full bg-[#f3f4f6] px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#475467] transition-colors hover:bg-[#e4e7ec]"
              >
                {copy.scheduleModal.close}
              </button>
            </div>

            <div className="custom-scrollbar overflow-y-auto px-7 py-6">
              <div className="space-y-3">
                {relatedTickets.map(item => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => {
                      setIsRelatedTicketsModalOpen(false);
                      onOpenTicket(item.id);
                    }}
                    className="related-ticket-card w-full rounded-[24px] border border-[rgba(0,0,0,0.04)] bg-[#fcfcfd] p-5 text-left transition-all hover:border-[rgba(0,105,73,0.14)] hover:bg-white hover:shadow-[0_16px_32px_rgba(15,23,42,0.06)] focus:outline-none focus:ring-2 focus:ring-[#006949]/15"
                    aria-label={copy.relatedTickets.openTicket(item.id)}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="flex flex-wrap items-center gap-2.5">
                        <p className="text-sm font-bold text-[#101828]">#{item.id}</p>
                        <span className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wide ${priorityClasses[item.priority]}`}>{copy.priorityLabels[item.priority]}</span>
                        <span className={`rounded-full bg-white px-3 py-1 text-[10px] font-bold uppercase tracking-wide shadow-[0_8px_20px_rgba(15,23,42,0.05)] ${statusClasses[item.status]}`}>{copy.statusLabels[item.status]}</span>
                        <span className="rounded-full bg-white px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-[#667085] shadow-[0_8px_20px_rgba(15,23,42,0.05)]">{item.category}</span>
                      </div>
                    </div>
                    <div className="mt-3">
                      <p className="text-sm font-medium leading-6 text-[#475467]">{item.subject}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
      </AnimatedModal>

      <AnimatedModal
        open={Boolean(selectedArticleModal)}
        onClose={() => setSelectedArticleModal(null)}
        backdropAriaLabel={copy.articlePreview.closeLabel}
        dialogLabelledBy="linked-article-preview-title"
        overlayClassName="fixed inset-0 z-[85] flex items-center justify-center bg-[#101828]/40 p-6 backdrop-blur-[4px]"
        dialogClassName="app-modal-dialog relative z-[1] flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-[32px] border border-white/70 bg-white shadow-[0_32px_64px_rgba(15,23,42,0.22)]"
      >
        {selectedArticleModal ? (
          <>
            <div className="flex flex-wrap items-start justify-between gap-4 border-b border-[#eaecf0] px-8 py-6">
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">{copy.articlePreview.title}</p>
                <h2 id="linked-article-preview-title" className="mt-2 font-headline text-3xl font-extrabold tracking-[-0.05em] text-[#101828]">
                  {selectedArticleModal.title}
                </h2>
                <p className="mt-3 max-w-3xl text-sm text-[#667085]">{selectedArticleModal.summary || copy.articlePreview.noSummary}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <span className="rounded-full bg-[#f2f4f7] px-3 py-1 text-[11px] font-semibold text-[#475467]">{selectedArticleModal.status}</span>
                  <span className="rounded-full bg-[#f2f4f7] px-3 py-1 text-[11px] font-semibold text-[#475467]">{selectedArticleModal.estimatedReadTime}</span>
                  <span className="rounded-full bg-[#f2f4f7] px-3 py-1 text-[11px] font-semibold text-[#475467]">{selectedArticleModal.audience}</span>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedArticleModal(null);
                    onOpenArticle(selectedArticleModal.id, selectedArticleModal.collectionId);
                  }}
                  className="rounded-xl bg-white px-4 py-3 text-[11px] font-bold uppercase tracking-[0.18em] text-[#475467] shadow-[0_8px_20px_rgba(15,23,42,0.04)] transition-colors hover:bg-[#f8fafc]"
                >
                  {copy.articlePreview.openFullPage}
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedArticleModal(null)}
                  className="rounded-xl bg-[#f3f4f6] px-4 py-3 text-[11px] font-bold uppercase tracking-[0.18em] text-[#475467] transition-colors hover:bg-[#e4e7ec]"
                >
                  {copy.articlePreview.close}
                </button>
              </div>
            </div>

            <div className="overflow-y-auto px-8 py-6">
              <div className="mb-6 flex flex-wrap items-center gap-3">
                <span className="rounded-full bg-[#f9fafb] px-4 py-2 text-xs font-semibold text-[#475467]">{selectedArticleModal.meta}</span>
                <span className="rounded-full bg-[#f9fafb] px-4 py-2 text-xs font-semibold text-[#475467]">
                  {copy.articlePreview.created(new Intl.DateTimeFormat(getIntlLocale(language), { month: "short", day: "numeric", year: "numeric" }).format(new Date(selectedArticleModal.createdAt)))}
                </span>
              </div>

              <div className="space-y-6">
                {selectedArticleModal.content?.html ? (
                  <RichTextContentRenderer content={selectedArticleModal.content} />
                ) : (
                  <div className="rounded-[24px] border border-dashed border-[#d0d5dd] bg-[#fcfcfd] px-5 py-8 text-center">
                    <p className="text-sm font-semibold text-[#101828]">{copy.articlePreview.noStructuredContentTitle}</p>
                    <p className="mt-2 text-sm text-[#667085]">{copy.articlePreview.noStructuredContentDescription}</p>
                  </div>
                )}
              </div>
            </div>
          </>
        ) : null}
      </AnimatedModal>

      <AnimatedModal
        open={isCustomerModalOpen}
        onClose={closeCustomerModal}
        overlayClassName="fixed inset-0 z-[85] flex items-center justify-center bg-[#101828]/40 p-6 backdrop-blur-[4px]"
        dialogClassName="app-modal-dialog relative z-[1] flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-[32px] border border-white/70 bg-white shadow-[0_32px_64px_rgba(15,23,42,0.22)]"
        backdropAriaLabel={copy.customer.closeDialog}
        dialogLabelledBy="ticket-customer-dialog-title"
        dialogDescribedBy="ticket-customer-dialog-description"
      >
        <div className="flex flex-wrap items-start justify-between gap-4 border-b border-[#eaecf0] px-8 py-6">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">{copy.customer.title}</p>
            <h2 id="ticket-customer-dialog-title" className="mt-2 font-headline text-3xl font-extrabold tracking-[-0.05em] text-[#101828]">
              {hasCustomer ? copy.customer.changeCustomerTitle : copy.customer.addCustomerTitle}
            </h2>
            <p id="ticket-customer-dialog-description" className="mt-2 text-sm text-[#667085]">
              {hasCustomer ? copy.customer.changeCustomerDescription : copy.customer.addCustomerDescription}
            </p>
          </div>
          <button
            type="button"
            onClick={closeCustomerModal}
            className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-lg font-semibold text-slate-500 shadow-[0_12px_24px_rgba(15,23,42,0.08)] transition-colors hover:bg-[#f3f4f6] hover:text-[#1a1c1d]"
            aria-label={copy.customer.closeDialog}
          >
            ×
          </button>
        </div>

        <div className="border-b border-[#eaecf0] px-8 py-5">
          <input
            type="search"
            value={customerSearch}
            onChange={event => setCustomerSearch(event.target.value)}
            placeholder={copy.customer.searchPlaceholder}
            className="w-full rounded-2xl border border-[#d0d5dd] bg-white px-4 py-3 text-sm text-[#101828] outline-none transition placeholder:text-[#98a2b3] focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-[var(--brand-primary-ring)]"
          />
        </div>

        <div className="overflow-y-auto px-8 py-6">
          <div className="space-y-3">
            <button
              type="button"
              onClick={() => setSelectedCustomerIdDraft("")}
              className={`w-full rounded-[24px] border px-5 py-4 text-left transition ${selectedCustomerIdDraft === "" ? "border-[var(--brand-primary)] bg-[var(--brand-primary-soft)]" : "border-[#eaecf0] bg-[#fcfcfd] hover:border-[#cfd4dc] hover:bg-white"}`}
            >
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-[#101828]">{copy.customer.noCustomerOption}</p>
                  <p className="mt-1 text-sm text-[#667085]">{copy.customer.noCustomerOptionDetail}</p>
                </div>
                {selectedCustomerIdDraft === "" ? <span className="text-sm font-bold text-[var(--brand-primary)]">✓</span> : null}
              </div>
            </button>

            {filteredCustomers.length > 0 ? filteredCustomers.map(item => {
              const isSelected = selectedCustomerIdDraft === item.id;

              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setSelectedCustomerIdDraft(item.id)}
                  className={`w-full rounded-[24px] border px-5 py-4 text-left transition ${isSelected ? "border-[var(--brand-primary)] bg-[var(--brand-primary-soft)]" : "border-[#eaecf0] bg-white hover:border-[#cfd4dc] hover:bg-[#fcfcfd]"}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-base font-semibold text-[#101828]">{item.company}</p>
                      <p className="mt-1 truncate text-sm text-[#475467]">{item.name || item.email || copy.none}</p>
                      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-[#667085]">
                        {item.email ? <span>{item.email}</span> : null}
                        {item.region ? <span>{item.region}</span> : null}
                      </div>
                    </div>
                    {isSelected ? <span className="text-sm font-bold text-[var(--brand-primary)]">✓</span> : null}
                  </div>
                </button>
              );
            }) : (
              <div className="rounded-[24px] border border-dashed border-[#d0d5dd] bg-[#fcfcfd] px-5 py-8 text-center">
                <p className="text-sm text-[#667085]">{copy.customer.noCustomerMatches}</p>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t border-[#eaecf0] px-8 py-6">
          <button
            type="button"
            onClick={closeCustomerModal}
            className="rounded-xl bg-[#f3f4f6] px-4 py-3 text-[11px] font-bold uppercase tracking-[0.18em] text-[#475467] transition-colors hover:bg-[#e4e7ec]"
          >
            {copy.customer.cancel}
          </button>
          <button
            type="button"
            onClick={handleSaveCustomer}
            disabled={selectedCustomerIdDraft === (hasCustomer ? customer.id : "")}
            className="brand-primary-button rounded-xl px-5 py-3 text-[11px] font-bold uppercase tracking-[0.18em] transition-all disabled:cursor-not-allowed disabled:opacity-60"
          >
            {copy.customer.saveCustomer}
          </button>
        </div>
      </AnimatedModal>

      <AnimatedModal
        open={Boolean(selectedAttachmentModal)}
        onClose={() => setSelectedAttachmentModal(null)}
        backdropAriaLabel={copy.attachments.closePreview}
        dialogLabelledBy="attachment-preview-title"
        overlayClassName="fixed inset-0 z-[86] flex items-center justify-center bg-[#101828]/40 p-6 backdrop-blur-[4px]"
        dialogClassName="app-modal-dialog relative z-[1] flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-[32px] border border-white/70 bg-white shadow-[0_32px_64px_rgba(15,23,42,0.22)]"
      >
        {selectedAttachmentModal ? (
          <>
            <div className="flex flex-wrap items-start justify-between gap-4 border-b border-[#eaecf0] px-8 py-6">
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">{copy.attachments.previewTitle}</p>
                <h2 id="attachment-preview-title" className="mt-2 truncate font-headline text-3xl font-extrabold tracking-[-0.05em] text-[#101828]">
                  {selectedAttachmentModal.name}
                </h2>
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className="rounded-full bg-[#f9fafb] px-4 py-2 text-xs font-semibold text-[#475467]">
                    {formatAttachmentSize(selectedAttachmentModal.size)}
                  </span>
                  <span className="rounded-full bg-[#f9fafb] px-4 py-2 text-xs font-semibold text-[#475467]">
                    {copy.attachments.uploadedAt(formatAttachmentTimestamp(selectedAttachmentModal.uploadedAt, language))}
                  </span>
                  {selectedAttachmentModal.contentType ? (
                    <span className="rounded-full bg-[#f9fafb] px-4 py-2 text-xs font-semibold text-[#475467]">{selectedAttachmentModal.contentType}</span>
                  ) : null}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <a
                  href={selectedAttachmentModal.downloadUrl}
                  className="ticket-attachments-action inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white text-[#475467] shadow-[0_8px_20px_rgba(15,23,42,0.04)] transition-colors hover:bg-[#f8fafc]"
                  aria-label={copy.attachments.downloadLabel(selectedAttachmentModal.name)}
                  title={copy.attachments.downloadLabel(selectedAttachmentModal.name)}
                >
                  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-4 w-4" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10 3.75v7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M6.875 8.958 10 12.083l3.125-3.125" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M4.583 13.75v.417c0 .69.56 1.25 1.25 1.25h8.334c.69 0 1.25-.56 1.25-1.25v-.417" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span className="sr-only">{copy.attachments.downloadLabel(selectedAttachmentModal.name)}</span>
                </a>
                <button
                  type="button"
                  onClick={() => void handleCopyAttachmentLink(selectedAttachmentModal)}
                  aria-label={copy.attachments.copyLinkLabel(selectedAttachmentModal.name)}
                  title={copiedAttachmentKey === selectedAttachmentModal.key ? copy.attachments.linkCopied : copy.attachments.copyLinkLabel(selectedAttachmentModal.name)}
                  className={`ticket-attachments-action inline-flex h-10 w-10 items-center justify-center rounded-xl shadow-[0_8px_20px_rgba(15,23,42,0.04)] transition-colors ${copiedAttachmentKey === selectedAttachmentModal.key ? "bg-[#ecfdf3] text-[#067647]" : "bg-white text-[#475467] hover:bg-[#f8fafc]"}`}
                >
                  {copiedAttachmentKey === selectedAttachmentModal.key ? (
                    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-4 w-4" xmlns="http://www.w3.org/2000/svg">
                      <path d="M5 10.417 8.333 13.75 15 6.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-4 w-4" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8.333 10.833a2.5 2.5 0 0 0 3.767.27l1.667-1.666a2.5 2.5 0 0 0-3.536-3.536l-.955.95" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M11.667 9.167a2.5 2.5 0 0 0-3.767-.27L6.233 10.563a2.5 2.5 0 0 0 3.536 3.536l.95-.95" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                  <span className="sr-only">{copiedAttachmentKey === selectedAttachmentModal.key ? copy.attachments.linkCopied : copy.attachments.copyLinkLabel(selectedAttachmentModal.name)}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedAttachmentModal(null)}
                  className="rounded-xl bg-[#f3f4f6] px-4 py-3 text-[11px] font-bold uppercase tracking-[0.18em] text-[#475467] transition-colors hover:bg-[#e4e7ec]"
                >
                  {copy.attachments.close}
                </button>
              </div>
            </div>

            <div className="overflow-y-auto px-8 py-6">
              {(() => {
                const previewKind = getAttachmentPreviewKind(selectedAttachmentModal);

                if (previewKind === "image") {
                  return (
                    <div className="flex justify-center rounded-[28px] bg-[#f9f9fa] p-6">
                      <img src={selectedAttachmentModal.downloadUrl} alt={selectedAttachmentModal.name} className="max-h-[68vh] w-auto max-w-full rounded-[20px] object-contain shadow-[0_16px_32px_rgba(15,23,42,0.08)]" />
                    </div>
                  );
                }

                if (previewKind === "video") {
                  return (
                    <div className="rounded-[28px] bg-[#f9f9fa] p-6">
                      <video src={selectedAttachmentModal.downloadUrl} controls className="max-h-[68vh] w-full rounded-[20px] bg-black" />
                    </div>
                  );
                }

                if (previewKind === "audio") {
                  return (
                    <div className="rounded-[28px] bg-[#f9f9fa] p-8">
                      <audio src={selectedAttachmentModal.downloadUrl} controls className="w-full" />
                    </div>
                  );
                }

                if (previewKind === "pdf") {
                  if (isLoadingAttachmentPdfPreview) {
                    return (
                      <div className="rounded-[28px] border border-[#e4e7ec] bg-white px-6 py-10 text-center">
                        <p className="text-sm font-semibold text-[#101828]">{copy.attachments.loadingPreview}</p>
                      </div>
                    );
                  }

                  if (attachmentPdfPreviewError) {
                    return (
                      <div className="rounded-[28px] border border-dashed border-[#d0d5dd] bg-[#f9f9fa] px-6 py-10 text-center">
                        <p className="text-sm font-semibold text-[#101828]">{copy.attachments.pdfUnavailableTitle}</p>
                        <p className="mt-2 text-sm text-[#667085]">{attachmentPdfPreviewError}</p>
                      </div>
                    );
                  }

                  return (
                    <div className="attachment-preview-frame overflow-hidden rounded-[28px] border border-[#e4e7ec] bg-white">
                      <iframe title={selectedAttachmentModal.name} src={attachmentPdfPreviewUrl ?? undefined} className="h-[68vh] w-full border-0" />
                    </div>
                  );
                }

                if (previewKind === "text") {
                  if (isLoadingAttachmentTextPreview) {
                    return (
                      <div className="rounded-[28px] border border-[#e4e7ec] bg-white px-6 py-10 text-center">
                        <p className="text-sm font-semibold text-[#101828]">{copy.attachments.loadingPreview}</p>
                      </div>
                    );
                  }

                  if (attachmentTextPreviewError) {
                    return (
                      <div className="rounded-[28px] border border-dashed border-[#d0d5dd] bg-[#f9f9fa] px-6 py-10 text-center">
                        <p className="text-sm font-semibold text-[#101828]">{copy.attachments.textUnavailableTitle}</p>
                        <p className="mt-2 text-sm text-[#667085]">{attachmentTextPreviewError}</p>
                      </div>
                    );
                  }

                  return (
                    <div className="overflow-hidden rounded-[28px] border border-[#e4e7ec] bg-[#fcfcfd] shadow-[0_16px_32px_rgba(15,23,42,0.04)]">
                      <pre className="max-h-[68vh] overflow-auto px-6 py-5 font-mono text-[13px] leading-6 text-[#101828] whitespace-pre-wrap break-words">{attachmentTextPreview ?? ""}</pre>
                    </div>
                  );
                }

                return (
                  <div className="rounded-[28px] border border-dashed border-[#d0d5dd] bg-[#f9f9fa] px-6 py-10 text-center">
                    <p className="text-sm font-semibold text-[#101828]">{copy.attachments.previewUnavailableTitle}</p>
                    <p className="mt-2 text-sm text-[#667085]">{copy.attachments.previewUnavailableDescription}</p>
                  </div>
                );
              })()}
            </div>
          </>
        ) : null}
      </AnimatedModal>
    </section>
  );
}